import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Panel, Eyebrow } from '../../../components/Panel.tsx';
import { Button } from '../../../components/Button.tsx';
import { Input, Select } from '../../../components/Field.tsx';
import { StatusDot } from '../../../components/StatusDot.tsx';
import { Empty, ErrorLine, Awaiting } from '../../../components/Empty.tsx';
import { KeyValue, type KVItem } from '../../../components/KeyValue.tsx';
import { duration, NO_VALUE, relative, utc, utcTime } from '../../../components/format.ts';
import { useNow } from '../../../components/useNow.ts';
import { notifyOk } from '../../../components/toastStore.ts';
import {
  LIVENESS_STATE, STATUS_STATE, matrixTone, type Tone,
} from '../../../components/state.ts';
import { Icon } from '../../../shell/Icon.tsx';
import { isTypingTarget } from '../../../shell/TopBar.tsx';
import {
  useIssueCommand,
  useRobotCommands,
  type Command,
  type CommandName,
  type CommandStatus,
} from '../../../hooks/useCommands.ts';
import { useCommandStream } from '../../../hooks/useCommandStream.ts';
import { useRobot } from '../../../hooks/useRobot.ts';
import type { Liveness } from '../../../hooks/useRobots.ts';
import {
  CMD_HINT,
  CMD_STATUSES,
  CMD_TONE,
  IN_FLIGHT,
  bandNote,
  detailOf,
  failureOf,
  formatParam,
  hazardConsequence,
  hazardTone,
  ladderOf,
  latencyBand,
  latencyMs,
  paramDigest,
  profileVersion,
  readProfile,
  specFor,
  waitBand,
  worstBand,
  type Band,
  type Ladder,
  type ParamSpec,
  type ProfileVerb,
} from '../../commands/commandModel.ts';
import { BandedAge } from '../../commands/BandedAge.tsx';
import { LifecycleTrack } from '../../commands/LifecycleTrack.tsx';
import { TransitionTrace } from '../../commands/TransitionTrace.tsx';
import {
  AUDIT_WINDOW,
  useCommandAudit,
  useRefreshCommands,
  type CommandEvent,
  type LedgerCommand,
} from '../../commands/useCommandLedger.ts';
import { FeedTag, StaleLine } from '../../commands/FeedTag.tsx';
import { health, source, type Health } from '../../commands/freshness.ts';
import {
  FaultLine,
  Figure,
  figure,
  unread,
  useRead,
  type Read,
} from '../../../components/states/index.ts';
import { useScrollEdges } from '../../commands/useScrollEdges.ts';
import styles from './CommandPanel.module.css';

/* ==========================================================================
   DISPATCH — the C2 write path for one robot.

   Three rules, taken straight off the references, and the whole panel is
   built to hold them:

   1. NOTHING LEAVES THE BROWSER ON A CLICK. Picking a verb STAGES it; the
      parameters are then editable as raw typed numbers in an inspector; and
      only a pinned `CANCEL` / `SEND` pair of equal size commits
      (formant-3d-waypoint-path-dispatch.png). The commit verb never shares a
      row with anything that can lose work.

   2. THE VERB SET IS THE ROBOT'S OWN. It comes from `command_profile`, which
      the agent publishes on enrolment — never from a hard-coded list. A koko
      offers `climb`/`grip`/`cut`; a wukong does not, and never renders them
      greyed out as if it might.

   3. THE CONSEQUENCE IS ON SCREEN BEFORE THE COMMIT. The profile's hazard
      class gates a motion or hazardous verb behind an ARM switch — a switch,
      because a switch shows its state when nobody is looking at it — and the
      pre-flight block states the robot's live condition (link, mode, battery,
      what is already queued) in the robot's own readings.

   Every verb prints its keyboard shortcut inline, the way a device context
   menu does in Formant. 1–9 stage; ⌘⏎ commits; Esc stands the panel down.

   The panel is deliberately self-contained and takes only a robot id, so the
   same control serves the fleet queue's dispatch rail and a robot's own
   detail page.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Parameter editing
   -------------------------------------------------------------------------- */

/** The edit buffer for one parameter. Strings until commit — a half-typed
    `-12.` is a legal thing to have in a field and an illegal number. */
type Draft = string | boolean | { lat: string; lon: string };

interface ParamRow {
  name: string;
  spec: ParamSpec;
  required: boolean;
  /** Where the initial value came from, printed under the field. */
  seed: string | null;
}

function draftFor(spec: ParamSpec, value: unknown): Draft {
  if (spec.kind === 'bool') return value === true;
  if (spec.kind === 'latlon') {
    const o = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
    return {
      lat: typeof o['lat'] === 'number' ? String(o['lat']) : '',
      lon: typeof o['lon'] === 'number' ? String(o['lon']) : '',
    };
  }
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Nothing typed here yet. A boolean is never blank — false is a value. */
function isBlank(d: Draft): boolean {
  if (typeof d === 'boolean') return false;
  if (typeof d === 'string') return d.trim() === '';
  return d.lat.trim() === '' && d.lon.trim() === '';
}

/** `ok: false` = this draft cannot be sent. The caller shows why, per field. */
function coerce(spec: ParamSpec, d: Draft): { ok: true; value: unknown } | { ok: false; why: string } {
  if (spec.kind === 'bool') return { ok: true, value: d === true };
  if (spec.kind === 'latlon') {
    const o = d as { lat: string; lon: string };
    const lat = Number(o.lat);
    const lon = Number(o.lon);
    if (o.lat.trim() === '' || o.lon.trim() === '') return { ok: false, why: 'lat and lon required' };
    if (!Number.isFinite(lat) || Math.abs(lat) > 90) return { ok: false, why: 'lat out of range' };
    if (!Number.isFinite(lon) || Math.abs(lon) > 180) return { ok: false, why: 'lon out of range' };
    return { ok: true, value: { lat, lon } };
  }
  const s = String(d);
  if (spec.kind === 'number') {
    if (s.trim() === '') return { ok: false, why: 'required' };
    const n = Number(s);
    if (!Number.isFinite(n)) return { ok: false, why: 'not a number' };
    if (spec.min !== undefined && n < spec.min) return { ok: false, why: `min ${spec.min}` };
    if (spec.max !== undefined && n > spec.max) return { ok: false, why: `max ${spec.max}` };
    return { ok: true, value: n };
  }
  if (spec.kind === 'json') {
    if (s.trim() === '') return { ok: false, why: 'required' };
    try {
      return { ok: true, value: JSON.parse(s) };
    } catch {
      return { ok: false, why: 'not valid JSON' };
    }
  }
  if (s.trim() === '') return { ok: false, why: 'required' };
  return { ok: true, value: s };
}

/* --------------------------------------------------------------------------
   Pre-flight — the robot's live condition, in its own readings
   -------------------------------------------------------------------------- */

interface Check {
  key: string;
  label: string;
  value: string;
  tone: Tone;
  note: string | null;
}

/**
 * The link check's colour — the register's rule, in one place.
 *
 * Both composers in this file had their own copy of it, written out by hand as
 * `live ? 'nominal' : backfilling || stale ? 'warn' : 'offline'`. That is a
 * third statement of what `matrixTone` already says, and it had already drifted
 * from the surfaces around it: it painted a live link steel while the fleet
 * register, the catalog, telemetry, the robot page and teleop all paint it
 * green, and the two copies would have had to be found and edited together
 * every time the palette moved.
 *
 * So it defers: exception states take the projection, and `live` takes its own
 * colour, exactly as the register's Link column does.
 */
function linkCheckTone(liveness: Liveness): Tone {
  return liveness === 'live' ? 'live' : matrixTone(LIVENESS_STATE[liveness].tone);
}

/* --------------------------------------------------------------------------
   Actuation authority

   Some surfaces own a page-level actuation lease — the robot detail page takes
   one from a switch in its identity row, and that switch is on screen on every
   tab. Where such a lease exists it IS the arming gesture, and this panel must
   not ask for a second one: two arming controls for one act is two controls an
   operator learns to click through. So the panel's own switch is replaced by a
   readout of the authority it is being judged against, and the commit stays
   blocked, with the reason printed, until that authority is held.

   Where no lease exists (the fleet queue), the panel arms the hazard class
   itself, and the switch is its own.
   -------------------------------------------------------------------------- */

/**
 * A command the embedding surface wants STAGED — never sent.
 *
 * The queue's record pane can put an existing command back on the bench: same
 * verb, same parameters, same robot, in the editor, behind the same
 * `CANCEL`/`SEND` pair as anything else. It is the operator's most common act
 * after reading a failure, and the alternative — retyping a mission id off a
 * row eighty pixels away — is how the wrong id gets sent.
 *
 * `seq` is monotonic because re-issuing the SAME command twice must stage it
 * twice, and a request identified by its content would look unchanged.
 */
export interface StageRequest {
  seq: number;
  /** The robot the request is for. The panel ignores it until its own target
      has caught up, so a re-issue can never land on the previous machine. */
  robotId: string;
  verb: string;
  params: Record<string, unknown>;
  /** Where the values came from, printed under every field they seeded. */
  from: string;
}

export interface LeaseState {
  /** Is actuation authority held for this robot right now? */
  held: boolean;
  /** Milliseconds of authority left. Null when it is not held. */
  msLeft: number | null;
  /** Where the operator takes it, in words. Printed when it is not held. */
  where: string;
  /** Take it. The SAME lease the embedding page's own switch takes — one
      authority, rendered at the point of action as well as in the identity
      row, never a second thing to arm. */
  onAcquire: () => void;
  /** Release it. One click, never behind a confirmation (THE BAR §11.6). */
  onRelease: () => void;
}

/**
 * The width at which the panel stops being a rail and becomes the three-zone
 * inspector — verb set │ staged command │ commit rail. Kept in step with the
 * `@container dispatch (min-width: 940px)` rule in the stylesheet; both are
 * measured against the same element.
 */
const WIDE_PX = 940;
/* Below this the pane cannot hold an expanded pre-flight AND the standing
   queue a staged command is about to land behind. The queue wins: the notes
   under each check qualify a reading, the queue is the reading that says a
   `resume` is going in behind an unacked `stop`. */
const TIGHT_PX = 430;

/* --------------------------------------------------------------------------
   The panel
   -------------------------------------------------------------------------- */

export function CommandPanel({
  robotId,
  className,
  /**
   * What the panel's own 28px header says. Defaults to the word `Dispatch`.
   * The queue workspace hands it a two-segment tablist instead, because there
   * the header IS the switch between this pane and the record beside it —
   * a tab strip above a panel header would spend 52px of a 706px rail on
   * chrome that names the same thing twice.
   */
  title = 'Dispatch',
  /** A robot picker, when the surface embedding this one owns the target. */
  targetControl,
  /** Fires with the new command's id once the platform has accepted it. */
  onIssued,
  /**
   * Fires whenever a verb is staged or stood down. The embedding surface uses
   * it to give this panel the room a staged command needs — parameters,
   * precedent and a pre-flight do not fit in half a rail, and a safety block
   * an operator has to scroll to find is a safety block they will not read.
   */
  onStagedChange,
  /** A page-level actuation lease that stands in for this panel's own switch. */
  lease,
  /**
   * The threshold ladder the embedding surface's queue is inked against.
   * Passed in so the standing queue in this rail and the board beside it
   * recolour at the same two edges; omitted, the panel measures the robot's
   * own settled history instead, which is what a standalone embed has.
   */
  ladder,
  /** A verb the embedding surface wants staged, with its parameters. */
  stageRequest,
}: {
  robotId: string | null | undefined;
  className?: string | undefined;
  title?: ReactNode | undefined;
  targetControl?: ReactNode | undefined;
  onIssued?: ((id: number) => void) | undefined;
  onStagedChange?: ((verb: string | null) => void) | undefined;
  lease?: LeaseState | undefined;
  ladder?: Ladder | undefined;
  stageRequest?: StageRequest | null | undefined;
}) {
  const robotQ = useRobot(robotId ?? undefined);
  const historyQ = useRobotCommands(robotId ?? undefined);
  /* The fleet-wide audit, for parameter-shape precedent only (see shapeOf).
     Same query key the board uses, so React Query serves it from cache. */
  const auditQ = useCommandAudit();
  const { data: robot } = robotQ;
  const { data: history } = historyQ;
  const { connected } = useCommandStream(robotId ?? undefined);
  const issue = useIssueCommand();
  const refresh = useRefreshCommands();
  const clock = useNow();

  /* ---- this panel's own freshness ----------------------------------------
     Dispatch reads two endpoints nothing else on the surface reads, at two
     different cadences, so it gets its own answer rather than inheriting the
     board's (§13 tell 27). It matters more here than anywhere: the pre-flight
     block below is the operator's basis for judging whether a motion command
     is safe, and a pre-flight built on a dead read is worse than no
     pre-flight at all — it looks like a check that passed. */
  const feed = health(
    [
      source(`/api/robots/${robotId ?? ''}`, 5_000, robotQ),
      source(`/api/robots/${robotId ?? ''}/commands`, 20_000, historyQ),
    ],
    clock,
  );
  /* THE SAME TWO SOURCES, AS `components/states` WORDS THEM. `Health.live` is
     true before anything has landed, so the tag it drives claimed a live
     cadence on every cold start; these are what let it say `Reading` and
     `no reply` instead. */
  const robotRead = useRead(robotQ, {
    path: robotId == null ? undefined : `/api/robots/${robotId}`,
    everyMs: 5_000,
    subject: robotId ?? 'this robot',
  });
  const historyRead = useRead(historyQ, {
    path: robotId == null ? undefined : `/api/robots/${robotId}/commands`,
    everyMs: 20_000,
    subject: 'the command history',
  });
  /* Every age this panel draws off a row is measured against the last good
     read, not the wall clock, so a frozen `Ahead of this` queue cannot show
     an age that keeps growing. The chip's own age uses `clock`. */
  const now = robotId == null ? clock : feed.asOf;

  const [staged, setStaged] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  /* Set only when the buffer was seeded from a specific past command rather
     than from this robot's most recent issue of the verb, so every field can
     name the command its value actually came from. */
  const [restaged, setRestaged] = useState<{ from: string; params: Record<string, unknown> } | null>(null);
  const [armed, setArmed] = useState(false);
  /* With a verb staged the list folds to that one row and the inspector takes
     the panel: a 328px rail cannot hold ten verbs, four parameter fields and
     a pre-flight block at once, and the parameters are what the operator came
     here to edit. `change` reopens the list; so does any number key. */
  const [browsing, setBrowsing] = useState(false);
  /* THE LAST THING THIS BENCH SENT, AND WHAT HAS HAPPENED TO IT SINCE.
     Committing stands the bench down, which is right — the act is finished —
     but it also left the operator's only confirmation as a toast and a row on
     a board 400px away. The receipt keeps the id, the verb and the live
     time-in-state at the commit, where the press happened, until the next
     staging replaces it. */
  const [receipt, setReceipt] = useState<{ id: number; verb: string; seed: Command } | null>(null);

  useEffect(() => {
    onStagedChange?.(staged);
  }, [staged, onStagedChange]);

  const profile = useMemo(() => readProfile(robot?.command_profile), [robot?.command_profile]);
  const version = profileVersion(robot?.command_profile);

  /* Standing down whenever the target changes is not a nicety: a staged
     `cut` that survived a robot switch would send a chainsaw command to
     whatever robot happened to be selected next. */
  useEffect(() => {
    setStaged(null);
    setDrafts({});
    setRestaged(null);
    setArmed(false);
    setBrowsing(false);
    setReceipt(null);
  }, [robotId]);

  const verb = useMemo(
    () => (staged === null ? null : (profile ?? []).find((v) => v.name === staged) ?? null),
    [staged, profile],
  );

  /* The most recent issue of this verb ON THIS ROBOT. It is the only source
     the platform has for a parameter's *type* beyond the required-name list,
     and it doubles as the sane default: an operator re-issuing `start_capture`
     nearly always wants the same session label. */
  const lastOfVerb = useMemo(
    () => (staged === null ? null : (history ?? []).find((c) => c.command === staged) ?? null),
    [history, staged],
  );

  /**
   * FLEET-WIDE PRECEDENT FOR A VERB'S PARAMETER SHAPE.
   *
   * `/api/command-profiles` publishes `required_params` and nothing else, and
   * for most verbs it publishes an EMPTY list — koko's `climb` declares no
   * parameters, while every `climb` the fleet has actually issued carried
   * `target_height_m`. On a robot that has run the verb before, this panel
   * already learns the shape from that robot's own history. On one that has
   * not, it used to say "No parameters. The verb is the whole command.",
   * which is a stronger claim than the contract supports and quietly removes
   * the operator's ability to set a height at all.
   *
   * So it falls back to the newest issue of the same verb on ANY robot, in
   * `/api/audit/commands` — the same read the board runs, deduped by query
   * key, so it costs no request.
   *
   * The SHAPE transfers; the VALUE does not. A climb height from another
   * machine is not a default for this one, and seeding it would be exactly
   * the casual dispatch this panel exists to prevent. The field appears
   * empty, optional, and labelled with the command it was learned from.
   */
  const shapeOf = useCallback(
    (name: string) => {
      let best: { params: Record<string, unknown>; id: number; robot: string; ts: number } | null = null;
      for (const e of auditQ.data ?? []) {
        if (e.event !== 'issued' || e.robot_id === robotId) continue;
        if (e.fields['command'] !== name) continue;
        const raw = e.fields['params'];
        if (typeof raw !== 'object' || raw === null) continue;
        const params = raw as Record<string, unknown>;
        if (Object.keys(params).length === 0) continue;
        const ts = Date.parse(e.ts);
        if (best === null || ts > best.ts) {
          best = { params, id: e.command_id, robot: e.robot_id, ts };
        }
      }
      return best;
    },
    [auditQ.data, robotId],
  );

  /**
   * HOW THIS VERB HAS GONE ON THE REST OF THE FLEET.
   *
   * The `Precedent` block above answers "how did this go HERE", and on a
   * machine that has never run the verb its honest answer is one sentence of
   * absence — which is exactly the moment the operator most needs a reading.
   * `koko-03` has never been sent `climb`; the fleet has sent it nine times and
   * four of those ended in `BATTERY_FLOOR`. That is a fact about the verb, not
   * about the machine, and it belongs in front of the commit.
   *
   * Folded out of `/api/audit/commands` — the same read the board runs and the
   * same one `shapeOf` already consults, deduped by query key, so it costs no
   * request. An `issued` event carries the verb; the newest event on the same
   * command id that carries a status carries the outcome. Nothing is
   * aggregated into a rate and nothing is inferred: a command whose issue has
   * fallen out of the audit window simply is not counted, and the block says
   * how wide the window it counted is.
   */
  const fleetPrecedent = useMemo(() => {
    if (staged === null) return null;
    const events = auditQ.data ?? [];
    const byCmd = new Map<number, CommandEvent[]>();
    for (const e of events) {
      const list = byCmd.get(e.command_id);
      if (list === undefined) byCmd.set(e.command_id, [e]);
      else list.push(e);
    }
    const runs: {
      id: number;
      robot: string;
      status: CommandStatus;
      at: number;
      code: string | null;
    }[] = [];
    for (const e of events) {
      if (e.event !== 'issued' || e.fields['command'] !== staged) continue;
      if (e.robot_id === robotId) continue;
      let status: CommandStatus = 'pending';
      let at = Date.parse(e.ts);
      let result: Record<string, unknown> | null = null;
      for (const h of byCmd.get(e.command_id) ?? []) {
        const t = Date.parse(h.ts);
        if (h.status !== null && t >= at) {
          status = h.status;
          at = t;
        }
        if (h.result !== null) result = h.result;
      }
      runs.push({
        id: e.command_id,
        robot: e.robot_id,
        status,
        at,
        code: failureOf(result)?.code ?? null,
      });
    }
    runs.sort((a, b) => b.at - a.at);
    return { runs, failed: runs.filter((r) => r.status === 'failed').length };
  }, [auditQ.data, staged, robotId]);

  /* Only consulted where this robot has no precedent of its own. */
  const shape = useMemo(
    () => (staged === null || lastOfVerb !== null ? null : shapeOf(staged)),
    [staged, lastOfVerb, shapeOf],
  );

  const rows = useMemo<ParamRow[]>(() => {
    if (verb === null) return [];
    /* A replay names its own source. It outranks the robot's most recent
       issue of the verb because the operator asked for THAT command, and a
       field silently carrying a newer value than the row they clicked is the
       casual dispatch this panel exists to prevent. */
    const observed = restaged?.params ?? lastOfVerb?.params ?? {};
    const learned = restaged !== null ? {} : (shape?.params ?? {});
    const names = [...new Set([...verb.required, ...Object.keys(observed), ...Object.keys(learned)])];
    return names.map((name) => {
      const spec = specFor(name, observed[name] ?? learned[name]);
      return {
        name,
        spec,
        required: verb.required.includes(name),
        seed:
          observed[name] !== undefined && restaged !== null
            ? `from ${restaged.from}`
            : observed[name] !== undefined && lastOfVerb !== null
              ? `from #${lastOfVerb.id}`
              : learned[name] !== undefined && shape !== null
                ? `shape · #${shape.id} ${shape.robot}`
                : spec.kind === 'latlon' && robot?.lat != null
                  ? `from last fix, ${relative(robot.last_seen, now)}`
                  : null,
      };
    });
  }, [verb, restaged, lastOfVerb, shape, robot?.lat, robot?.last_seen, now]);

  /* Staging builds the buffer once, from the profile plus history plus the
     robot's own last fix. After that it is the operator's to edit. */
  const stage = useCallback(
    (v: ProfileVerb, replay?: { from: string; params: Record<string, unknown> }) => {
      const own = (history ?? []).find((c) => c.command === v.name);
      const observed = replay?.params ?? own?.params ?? {};
      /* Same merge the row list does, so every field the panel draws has a
         draft behind it. Learned names contribute a TYPE, never a value. */
      const learned =
        replay === undefined && own === undefined ? (shapeOf(v.name)?.params ?? {}) : {};
      const names = [...new Set([...v.required, ...Object.keys(observed), ...Object.keys(learned)])];
      const next: Record<string, Draft> = {};
      for (const name of names) {
        const spec = specFor(name, observed[name] ?? learned[name]);
        let seed: unknown = observed[name];
        if (seed === undefined && spec.kind === 'latlon' && robot?.lat != null && robot.lon != null) {
          seed = { lat: robot.lat, lon: robot.lon };
        }
        next[name] = draftFor(spec, seed);
      }
      setStaged(v.name);
      setDrafts(next);
      setReceipt(null);
      setRestaged(replay ?? null);
      /* A replay is still a staging, never a commit: the class is stood back
         down and the operator arms it again for this dispatch. */
      setArmed(false);
      setBrowsing(false);
    },
    [history, shapeOf, robot?.lat, robot?.lon],
  );

  const standDown = useCallback(() => {
    setStaged(null);
    setDrafts({});
    setRestaged(null);
    setArmed(false);
    setBrowsing(false);
  }, []);

  /* A staging asked for from outside. It waits until this panel's own target
     has caught up with the request's, so switching robot and staging a verb
     can never happen in the wrong order, and it is applied once per `seq` —
     re-issuing the same command twice stages it twice, editing a field does
     not re-seed it from underneath the operator. */
  const appliedSeq = useRef(-1);
  useEffect(() => {
    if (stageRequest == null || stageRequest.seq === appliedSeq.current) return;
    if (robotId !== stageRequest.robotId) return;
    const v = (profile ?? []).find((x) => x.name === stageRequest.verb);
    if (v === undefined) return;
    appliedSeq.current = stageRequest.seq;
    stage(v, { from: stageRequest.from, params: stageRequest.params });
  }, [stageRequest, robotId, profile, stage]);

  /* ---- validity ---------------------------------------------------------- */

  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const r of rows) {
      const d = drafts[r.name];
      if (d === undefined) {
        if (r.required) out[r.name] = 'required';
        continue;
      }
      /* An optional parameter left blank is simply not sent — only a
         required one, or a filled-in bad value, is an error. */
      if (!r.required && isBlank(d)) continue;
      const c = coerce(r.spec, d);
      if (!c.ok) out[r.name] = c.why;
    }
    return out;
  }, [rows, drafts]);

  const invalid = Object.keys(errors).length > 0;
  const gated = verb !== null && verb.hazard !== 'benign';
  /* The lease, where a surface owns one, IS the arming. Where it does not,
     this panel's own switch is. Never both. */
  const authorised = lease === undefined ? armed : lease.held;
  const ready = verb !== null && !invalid && (!gated || authorised) && !issue.isPending;

  const blockedBecause = verb === null
    ? 'no verb staged'
    : invalid
      ? 'a parameter is missing or out of range'
      : gated && !authorised
        ? lease === undefined
          ? `arm the ${verb.hazard} class first`
          : 'actuation lease not held'
        : issue.isPending
          ? 'in flight'
          : null;

  /* ---- pre-flight -------------------------------------------------------- */

  /* What is already in this robot's queue. A count alone ("queued 4") tells
     the operator there is traffic but not whether the thing they are about to
     send contradicts it — a `resume` behind an unacked `stop` is the case
     that matters, and it is only visible as a list. */
  const inFlightHere = useMemo(
    () => (history ?? []).filter((c) => IN_FLIGHT.has(c.status)),
    [history],
  );
  const queued = inFlightHere.length;

  /* Fall back to the robot's own settled history when nobody handed a ladder
     down. `ladderOf` says out loud when a sample is too thin to measure. */
  const ownLadder = useMemo(() => {
    const samples: number[] = [];
    for (const c of history ?? []) {
      const ms = latencyMs(c);
      if (ms !== null) samples.push(ms);
    }
    samples.sort((a, b) => a - b);
    return ladderOf(samples);
  }, [history]);
  const bands = ladder ?? ownLadder;
  /* Staged, the whole standing queue matters — what a `resume` is about
     to land behind. Idle the same list is simply what is outstanding. Both
     are capped only against a runaway queue; the pane scrolls before the cap
     bites. */
  const ahead = inFlightHere.slice(0, staged === null ? 6 : 12);

  /* WHAT THIS MACHINE HAS ACTUALLY BEEN ASKED TO DO, AND HOW IT WENT.

     At rest the bench's question is not "what verbs exist" — the keypad
     answers that — it is "what has been happening on this robot". The
     settled tail of its own ledger answers it in one glance: a `stop_mapping`
     that failed with NODE_DOWN four hours ago is the reason the operator is
     standing here. Straight off /api/robots/{id}/commands, newest first.

     It yields the moment a verb is staged: then the space belongs to that
     command's parameters, its precedent and the queue it lands behind. And it
     is a rail-only block — on a robot's own page the full ledger is a table
     twenty rows tall directly below this panel, and printing eight of those
     rows twice on one screen is not density, it is noise. */
  const settledHere = useMemo(
    () => (history ?? []).filter((c) => !IN_FLIGHT.has(c.status)).slice(0, 5),
    [history],
  );

  /* The receipt's LIVE row: the ledger's copy the moment it carries one, the
     POST's own response until then. Never a status this panel invented. */
  const receiptRow = useMemo<Command | null>(
    () => (receipt === null ? null : (history ?? []).find((c) => c.id === receipt.id) ?? receipt.seed),
    [receipt, history],
  );

  /* WHAT EACH VERB HAS ACTUALLY DONE ON THIS MACHINE.

     The settled list above is a narrative — the last five things that
     happened. This is the other axis, and it is the one the bench is organised
     around: the operator is about to pick a VERB, and the question before
     picking it is "how has this verb gone here". Three runs of `stop_mapping`,
     the most recent of them failed four hours ago, is the fact that decides
     whether the next press is a re-issue or a call to someone.

     It also covers what a list of past commands structurally cannot: a verb
     the robot publishes and has NEVER been sent. `never issued` is a reading,
     not an absence — it is why the operator is about to be the first.

     Every figure is counted off `/api/robots/{id}/commands`, the same read the
     queue above it draws; nothing is aggregated into a rate. Verbs the robot
     has run but no longer publishes are kept and marked, because a history
     that silently drops rows is not a history. */
  const verbLedger = useMemo(() => {
    const byVerb = new Map<string, Command[]>();
    for (const c of history ?? []) {
      const list = byVerb.get(c.command);
      if (list === undefined) byVerb.set(c.command, [c]);
      else list.push(c);
    }
    const newest = (name: string): Command | null =>
      (byVerb.get(name) ?? []).reduce<Command | null>(
        (acc, c) =>
          acc === null || Date.parse(c.updated_at) > Date.parse(acc.updated_at) ? c : acc,
        null,
      );
    const out = (profile ?? []).map((v) => ({
      name: v.name,
      verb: v as ProfileVerb | null,
      n: byVerb.get(v.name)?.length ?? 0,
      last: newest(v.name),
    }));
    const known = new Set(out.map((l) => l.name));
    for (const name of byVerb.keys()) {
      if (known.has(name) || name === '') continue;
      out.push({ name, verb: null, n: byVerb.get(name)?.length ?? 0, last: newest(name) });
    }
    /* Most recently exercised first; everything the robot advertises and
       nobody has sent it falls to the bottom, in the profile's own order. A
       pane that opens on four rows of `never issued` has buried the rows that
       carry a reading. */
    out.sort((a, b) => {
      if (a.last === null || b.last === null) return (a.last === null ? 1 : 0) - (b.last === null ? 1 : 0);
      return Date.parse(b.last.updated_at) - Date.parse(a.last.updated_at);
    });
    return out;
  }, [history, profile]);

  /* THE TARGET'S OWN IDENTITY, off the registry row.

     Five facts the operator cannot infer from the robot id: the hardware it
     is, the build running on it, the contract revision that build publishes,
     where the machine physically stands, and how long its enrolment
     certificate has left — a cert that expires is a robot that stops
     accepting commands. Nothing is derived and nothing is guessed: a field
     `/api/robots/{id}` does not carry renders as the em dash. */
  const machine = useMemo<KVItem[]>(() => {
    if (robot === undefined) return [];
    const meta = robot.meta as Record<string, unknown>;
    const site = typeof meta['site'] === 'string' ? meta['site'] : null;
    const bay = typeof meta['bay'] === 'string' ? meta['bay'] : null;
    const kind = typeof meta['kind'] === 'string' ? meta['kind'] : null;
    const certLeft =
      robot.cert_not_after === null
        ? null
        : Date.parse(robot.cert_not_after) - now;
    const out: KVItem[] = [
      { label: 'Hardware', value: `${robot.morphology} · ${robot.model}` },
      {
        label: 'Build',
        value: robot.firmware ?? NO_VALUE,
        title: 'The build the robot published on its last check-in — its `firmware`, which its command profile republishes as `platform_version`',
      },
      {
        label: 'Profile',
        value: version === null ? NO_VALUE : version,
        title: 'The revision of the command contract this build publishes',
      },
      {
        label: 'Site',
        value: site === null ? NO_VALUE : bay === null ? site : `${site} · ${bay}`,
        ...(kind === null ? {} : { title: `Registered as ${kind}` }),
      },
      {
        label: 'Cert',
        mono: false,
        value:
          certLeft === null ? (
            NO_VALUE
          ) : (
            <span
              className={styles.certCell}
              data-tone={certLeft < 0 ? 'error' : certLeft < 30 * 86_400_000 ? 'warn' : undefined}
            >
              {certLeft < 0 ? 'expired' : `${duration(certLeft)} left`}
            </span>
          ),
        title:
          robot.cert_not_after === null
            ? 'No enrolment certificate on the registry row'
            : `X.509 enrolment certificate valid until ${utc(robot.cert_not_after)}. A robot whose certificate lapses stops authenticating and stops accepting commands.`,
      },
    ];
    /* The robot's OWN dispatch cost, measured over its own settled history —
       the same two numbers the queue prints for the fleet, so "is this machine
       slow, or is the fleet slow" is one glance. `ladderOf` says out loud when
       the sample is too thin to have measured a p95. */
    out.push({
      label: 'Issue → settle',
      mono: false,
      value: (
        <span className={styles.ladderCell}>
          <span className={styles.ladderP50}>{duration(ownLadder.p50)}</span>
          <span className={styles.ladderSep}>·</span>
          <span className={styles.ladderP95}>{duration(ownLadder.p95)}</span>
          <span className={styles.ladderN}>
            {ownLadder.measured ? `n ${ownLadder.n}` : 'fixed'}
          </span>
        </span>
      ),
      title: ownLadder.measured
        ? `Median and p95 issue → settle over the ${ownLadder.n} commands this robot has finished in the window.`
        : `Only ${ownLadder.n} settled command${ownLadder.n === 1 ? '' : 's'} on this robot in the window — too few to measure a p95, so these are the console's fixed edges rather than a measurement.`,
    });
    return out;
  }, [robot, version, now, ownLadder]);

  const checks = useMemo<Check[]>(() => {
    if (robot === undefined) return [];
    const out: Check[] = [];
    out.push({
      key: 'link',
      label: 'link',
      value: LIVENESS_STATE[robot.liveness].label,
      tone: linkCheckTone(robot.liveness),
      note:
        robot.liveness === 'live'
          ? robot.last_seen === null ? null : relative(robot.last_seen, now)
          : 'held until reconnect',
    });
    out.push({
      key: 'mode',
      label: 'mode',
      value: STATUS_STATE[robot.status].label,
      tone: matrixTone(STATUS_STATE[robot.status].tone),
      note: robot.status === 'error' ? 'motion will be rejected' : null,
    });
    out.push({
      key: 'battery',
      label: 'battery',
      value: robot.battery_pct === null ? NO_VALUE : `${robot.battery_pct.toFixed(0)}%`,
      tone:
        robot.battery_pct === null ? 'neutral'
          : robot.battery_pct <= 15 ? 'error'
            : robot.battery_pct <= 25 ? 'warn'
              : 'nominal',
      note: null,
    });
    return out;
  }, [robot, now]);

  /* ---- commit ------------------------------------------------------------ */

  const send = useCallback(() => {
    if (verb === null || robotId == null || !ready) return;
    const params: Record<string, unknown> = {};
    for (const r of rows) {
      const d = drafts[r.name];
      if (d === undefined) continue;
      if (!r.required && typeof d === 'string' && d.trim() === '') continue;
      const c = coerce(r.spec, d);
      if (c.ok) params[r.name] = c.value;
    }
    issue.mutate(
      { robotId, command: verb.name as CommandName, params },
      {
        onSuccess: (cmd) => {
          notifyOk(`${cmd.command} queued for ${cmd.robot_id}`, `#${cmd.id} · pending`);
          refresh(robotId);
          onIssued?.(cmd.id);
          standDown();
          setReceipt({ id: cmd.id, verb: cmd.command, seed: cmd });
        },
      },
    );
  }, [verb, robotId, ready, rows, drafts, issue, refresh, onIssued, standDown]);

  /* ---- how wide the host is ----------------------------------------------
     Read off the element the container query measures, so the layout and the
     folding decision can never disagree about which shape the panel is in. */
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [wide, setWide] = useState(false);
  const [tight, setTight] = useState(false);
  useEffect(() => {
    const el = frameRef.current;
    if (el === null || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box === undefined) return;
      setWide(box.width >= WIDE_PX);
      setTight(box.height < TIGHT_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ---- keyboard ----------------------------------------------------------
     Printed in the affordance, so nothing here is a secret. Digits stage the
     nth verb; ⌘⏎ commits; Esc stands down. Bare digits are safe — the console
     spends its two bare global keys on `l` and `/`. */
  const verbs = useMemo(() => profile ?? [], [profile]);
  /* FOLDING IS A CONSEQUENCE OF WIDTH, NOT OF STAGING. In a 328px rail the
     verb set plus four parameter fields plus a pre-flight do not fit at once,
     so staging folds the set to the one staged row and `change` reopens it.
     Given the three-zone width the same panel takes on a robot's own page,
     the set sits in its own column beside the parameters and there is nothing
     to fold — hiding nine verbs there would be hiding them for no reason. */
  const folded = verb !== null && !browsing && !wide;
  const shown: readonly ProfileVerb[] = folded ? [verb] : verbs;

  /* The set, split into the classes the profile itself publishes — never a
     fixed three, because a class the agent invents later must get its own
     header rather than be filed under the safest one on screen. `readProfile`
     has already ranked them, so this preserves both the reading order and the
     digit order. */
  const verbClasses = useMemo(() => {
    const out: { hazard: string; verbs: ProfileVerb[] }[] = [];
    for (const v of verbs) {
      const last = out[out.length - 1];
      if (last !== undefined && last.hazard === v.hazard) last.verbs.push(v);
      else out.push({ hazard: v.hazard, verbs: [v] });
    }
    return out;
  }, [verbs]);

  const needParams = useMemo(() => verbs.filter((v) => v.required.length > 0), [verbs]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && staged !== null) {
        standDown();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (ready) {
          e.preventDefault();
          send();
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (!/^[0-9]$/.test(e.key)) return;
      /* 1–9 then 0, so a ten-verb profile is entirely reachable from the
         number row without a modifier. */
      const n = e.key === '0' ? 10 : Number(e.key);
      const v = verbs[n - 1];
      if (v === undefined) return;
      e.preventDefault();
      stage(v);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [verbs, stage, staged, standDown, ready, send]);

  /* ---- render ------------------------------------------------------------ */

  /* Which end of the pane is hiding rows. On a short rail the verb set plus a
     staged command's parameters is taller than the pane, and a profile clipped
     mid-glyph reads as a broken render rather than as "scroll". */
  const { ref: paneRef, edge: paneEdge } = useScrollEdges<HTMLDivElement>();
  const { ref: paneMeterRef, view: paneView } = useScrollView<HTMLDivElement>();
  /* The verb list carries its own, because it is its own scroll box — see the
     note on `.verbs`. */
  const { ref: verbRef, edge: verbEdge } = useScrollEdges<HTMLDivElement>();

  /* One verb, drawn identically whether it is sitting in the two-column set or
     standing alone as the folded staged row — so staging never changes what a
     verb looks like, only where it is. */
  const verbButton = (v: ProfileVerb) => {
    const active = v.name === staged;
    const slot = verbs.indexOf(v);
    return (
      <button
        key={v.name}
        type="button"
        className={styles.verb}
        data-active={active}
        data-tone={hazardTone(v.hazard)}
        aria-pressed={active}
        title={`${v.name} — ${hazardConsequence(v.hazard)}${
          v.required.length === 0 ? '' : `. Requires ${v.required.join(', ')}.`
        }`}
        onClick={() => (active ? standDown() : stage(v))}
      >
        <span className={styles.verbTick} aria-hidden="true" />
        <span className={styles.verbName}>{v.name}</span>
        {v.required.length > 0 && (
          <span className={styles.verbReq} aria-hidden="true" title={`requires ${v.required.join(', ')}`}>
            ·
          </span>
        )}
        {slot < 10 && <kbd className={styles.kbd}>{(slot + 1) % 10}</kbd>}
      </button>
    );
  };

  /* The header holds the title, the transport word and one liveness tag and
     nothing else. The profile's identity belongs over the list it describes,
     not squeezed into a 328px header beside a robot picker. */
  const meta =
    robotId == null
      ? undefined
      : (
        <FeedTag
          health={feed}
          now={clock}
          pulse={connected}
          read={[robotRead, historyRead]}
        />
      );

  let body: ReactNode;
  if (robotId == null) {
    body = <Empty prompt>No robot selected. Pick one to dispatch to.</Empty>;
  } else if (robot === undefined) {
    body = <Empty>Reading {robotId}…</Empty>;
  } else {
    /* A MACHINE WITH NO PROFILE IS STILL A MACHINE.

       `jetrover-08` is registered, has a certificate, has a site and a bay,
       and has never phoned home — so there is no verb set and nothing can be
       dispatched. That used to blank the entire pane behind one hatched
       sentence and 530px of nothing, which answers the one question the
       operator did NOT ask. What the pane knows is still true and still worth
       reading: what the machine is, when it was enrolled, how long its
       certificate has, and whether anything is already queued against it. So
       only the verb set stands down; the rest of the bench renders. */
    body = (
      <>
        {profile === null ? (
          /* `Awaiting` is a full-bleed hatched block by design — it was built
             for a video pane that has no stream. In a 600px rail it would
             stretch to the whole column and park its sentence at the bottom
             edge, so it is held to its own content height here and sits where
             the first row would be. */
          <>
            <div className={styles.awaitBlock}>
              <Awaiting
                label="No command profile published"
                detail={`${robotId} advertises its verb set on enrolment; nothing has arrived yet.`}
              />
            </div>
            {/* WHY THERE IS NO VERB SET, IN THE REGISTRY'S OWN DATES.
                A machine with no profile is the one case where the operator's
                question is not "what can I send" but "what is this thing
                waiting on", and the answer is an enrolment record: when it was
                registered, whether the agent ever completed enrolment, who
                enrolled it, and whether it has ever been heard from. All four
                are on `/api/robots/{id}`; a field the registry does not carry
                renders as the em dash, and `never` is a reading, not a gap. */}
            <div className={styles.enrol}>
            <div className={styles.stageHead}>
              <Eyebrow>Enrolment</Eyebrow>
              <span className={styles.stageVerb}>{robot.robot_id}</span>
            </div>
            <KeyValue
              layout="rows"
              items={[
                { label: 'Registered', value: utc(robot.created_at) },
                {
                  label: 'Enrolled',
                  value: robot.enrolled_at === null ? NO_VALUE : utc(robot.enrolled_at),
                  title: 'When the agent completed X.509 enrolment. Until it does, it publishes no command profile.',
                },
                { label: 'By', value: robot.enrolled_by ?? NO_VALUE },
                {
                  label: 'First contact',
                  mono: false,
                  value:
                    robot.last_seen === null ? (
                      <span className={styles.neverSeen}>never</span>
                    ) : (
                      relative(robot.last_seen, now)
                    ),
                  title:
                    robot.last_seen === null
                      ? 'This robot has never checked in'
                      : `Last check-in ${utc(robot.last_seen)}`,
                },
              ]}
            />
            </div>
          </>
        ) : (
        <>
        <div className={styles.profileLine}>
          <Eyebrow>Profile</Eyebrow>
          <span className={styles.profileTag}>
            {robot.morphology}
            {version === null ? '' : ` ${version}`}
          </span>
          <span
            className={styles.profileTransport}
            data-tone={feed.live ? undefined : 'warn'}
            title={
              connected
                ? 'Queue transitions arrive over MQTT as they happen'
                : 'MQTT is unavailable here — the queue re-reads every 20s instead'
            }
          >
            {connected ? 'push' : 'poll 20s'}
          </span>
          {folded ? (
            <button
              type="button"
              className={styles.change}
              onClick={() => setBrowsing(true)}
              title="Show every verb this robot accepts"
            >
              change
              <Icon name="chevron-down" size={12} />
            </button>
          ) : (
            <span className={styles.profileCount}>{verbs.length} verbs</span>
          )}
        </div>
        {/* THE WHOLE PROFILE, AT REST, WITHOUT A SCROLL.
            A verb set is a SET — the operator's first question is what this
            machine accepts, and a list that shows five of ten answers it with
            "some of these". One column at a 26px pitch needed 260px of a rail
            that has ~120px to give, so the set below the fold was reachable
            only by a number the operator could not see. Two columns at a 22px
            pitch put a ten-verb profile in 110px and the question is answered
            at a glance.

            The classes are the reading order and the numbering order — the
            profile arrives ranked benign → motion → hazardous (readProfile),
            so 1–9,0 run left-to-right, top-to-bottom, and the class a verb
            belongs to is a header rather than a tick the operator has to
            decode. Consequence before selection, not only after it. */}
        <div
          className={styles.verbs}
          role="group"
          aria-label="Commands this robot accepts"
          /* The key sheet's proof for `1`–`0`. Named here rather than matched
             on a class hash or on button text, so renaming a verb cannot
             silently turn the printed shortcut into a lie. */
          data-kbd="commands-verbs"
          ref={verbRef}
          data-edge={verbEdge}
          data-folded={folded}
          /* NOTHING STAGED, NOTHING GIVES. The set is `flex: 0 1 auto` so that
             a STAGED command's parameters can borrow height from it — that is
             what its own scroll box is for. With nothing staged there is no
             claim on that height and the keypad is the whole point of the
             pane, so it holds its natural size and the pane below it scrolls
             instead. Without this, adding a block to the idle bench silently
             clipped two hazard classes off the bottom of the verb set. */
          data-idle={verb === null}
        >
          {folded
            ? shown.map((v) => verbButton(v))
            : verbClasses.map((g) => (
                <div key={g.hazard} className={styles.verbClass}>
                  <div
                    className={styles.verbClassHead}
                    data-tone={hazardTone(g.hazard)}
                    title={hazardConsequence(g.hazard)}
                  >
                    <span className={styles.verbClassWord}>{g.hazard}</span>
                    <span className={styles.verbClassRule} aria-hidden="true" />
                    <span className={styles.verbClassCount}>{g.verbs.length}</span>
                  </div>
                  <div className={styles.verbGrid}>
                    {g.verbs.map((v) => verbButton(v))}
                    {/* A class with an odd count would otherwise end on a
                        half-width rule; the grid stays rectangular so the
                        seams read as a table rather than as a torn edge. */}
                    {g.verbs.length % 2 === 1 && (
                      <span className={styles.verbBlank} aria-hidden="true" />
                    )}
                  </div>
                </div>
              ))}
        </div>
        {/* The names of the parameters the profile insists on, once, under the
            set — a 152px cell cannot carry `start_mission  mission_id  0` and
            the fact still belongs on screen before a verb is picked. */}
        {!folded && needParams.length > 0 && (
          <p className={styles.verbNeeds}>
            {needParams.map((v, i) => (
              <span key={v.name}>
                {i > 0 && <span className={styles.verbNeedsSep}> · </span>}
                <span className={styles.verbNeedsName}>{v.name}</span>
                <span className={styles.verbNeedsArrow}>←</span>
                {v.required.join(' ')}
              </span>
            ))}
          </p>
        )}
        </>
        )}

        <div className={styles.stage} data-staged={verb !== null}>
          {verb !== null && (
            <>
            <div className={styles.stageHead}>
              <Eyebrow>Parameters</Eyebrow>
              <span className={styles.stageVerb}>{verb.name}</span>
            </div>
            {rows.length === 0 ? (
              <p className={styles.none}>No parameters. The verb is the whole command.</p>
            ) : (
              <div className={styles.params}>
                {rows.map((r) => (
                  <ParamField
                    key={r.name}
                    row={r}
                    value={drafts[r.name] ?? draftFor(r.spec, undefined)}
                    error={errors[r.name] ?? null}
                    onChange={(next) => setDrafts((d) => ({ ...d, [r.name]: next }))}
                  />
                ))}
              </div>
            )}

            {/* PRECEDENT — the last time this verb went to THIS robot, and how
                it ended. It is the single most useful fact before a re-issue:
                a `stop_mapping` that failed with NODE_DOWN four hours ago is
                about to fail the same way, and nothing else on the panel says
                so. Straight off /api/robots/{id}/commands; no precedent means
                exactly that, never an assumption. */}
            <div className={styles.stageHead}>
              <Eyebrow>Precedent</Eyebrow>
              <span className={styles.stageVerb}>{robot.robot_id}</span>
            </div>
            {lastOfVerb === null ? (
              <p className={styles.none}>Not issued to this robot in the window.</p>
            ) : (
              <div
                className={styles.prec}
                data-tone={CMD_TONE[lastOfVerb.status]}
                data-stale={!feed.live}
              >
                <StatusDot tone={CMD_TONE[lastOfVerb.status]} />
                <span className={styles.precId}>#{lastOfVerb.id}</span>
                <span className={styles.precState}>{lastOfVerb.status}</span>
                <span className={styles.precAge}>{relative(lastOfVerb.updated_at, now)}</span>
                <span className={styles.precNote} title={detailOf(lastOfVerb).text}>
                  {detailOf(lastOfVerb).mark ?? ''}
                  {detailOf(lastOfVerb).mark !== null ? ' ' : ''}
                  {detailOf(lastOfVerb).text}
                </span>
              </div>
            )}

            {/* ---- THE SAME VERB, EVERYWHERE ELSE ------------------------
                What the block above cannot answer on a machine that has never
                run this verb — and cannot answer WELL on one that has run it
                once. `climb` failing four times out of nine across the fleet
                is a property of the verb and of the build it runs on, and the
                operator standing at the commit is the person who needs it. */}
            {fleetPrecedent !== null && (
              <>
                <div className={styles.stageHead}>
                  <Eyebrow>Elsewhere</Eyebrow>
                  <span className={styles.stageVerb}>
                    {fleetPrecedent.runs.length === 0
                      ? verb.name
                      : `${fleetPrecedent.runs.length} run${fleetPrecedent.runs.length === 1 ? '' : 's'}`}
                    {fleetPrecedent.failed > 0 && (
                      <span className={styles.fpFailed}> · {fleetPrecedent.failed} failed</span>
                    )}
                  </span>
                </div>
                {fleetPrecedent.runs.length === 0 ? (
                  <p className={styles.none}>
                    No other robot has been sent {verb.name} in the {AUDIT_WINDOW}-event window.
                  </p>
                ) : (
                  <div className={styles.ahead} data-stale={!feed.live}>
                    {fleetPrecedent.runs.slice(0, 6).map((r) => (
                      <div key={r.id} className={styles.aheadRow} data-tone={CMD_TONE[r.status]}>
                        <StatusDot tone={CMD_TONE[r.status]} />
                        <span className={styles.precId}>#{r.id}</span>
                        <span className={styles.aheadVerb}>{r.robot}</span>
                        <span className={styles.precState}>{r.status}</span>
                        {r.code !== null && <span className={styles.fpCode}>{r.code}</span>}
                        <span className={styles.precAge}>{relative(new Date(r.at).toISOString(), now)}</span>
                      </div>
                    ))}
                    {fleetPrecedent.runs.length > 6 && (
                      <p className={styles.none}>
                        +{fleetPrecedent.runs.length - 6} more in the window.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            </>
          )}

            {/* The robot's own queue, not a number for it. With a verb staged
                the question is what it will land BEHIND — a `resume` queued
                behind an unacked `stop` is the case that matters — so the
                block is named for that. Idle, the same list is simply what is
                outstanding on the machine, and it is named for that instead.
                One block, one source, two honest labels. */}
            <div className={styles.stageHead}>
              <Eyebrow>{verb === null ? 'In flight' : 'Ahead of this'}</Eyebrow>
              <span className={styles.stageVerb}>{queued}</span>
            </div>
            {queued === 0 ? (
              <p className={styles.none}>Nothing outstanding on {robot.robot_id}.</p>
            ) : (
              <div className={styles.ahead} data-stale={!feed.live}>
                {ahead.map((c) => (
                  <div key={c.id} className={styles.aheadRow} data-tone={CMD_TONE[c.status]}>
                    <StatusDot tone={CMD_TONE[c.status]} />
                    <span className={styles.precId}>#{c.id}</span>
                    <span className={styles.aheadVerb}>{c.command}</span>
                    <span className={styles.precState}>{c.status}</span>
                    {/* The same ink the board's `In state` column uses: the
                        queue this command is about to land behind is exactly
                        where a stuck predecessor matters most. */}
                    <BandedAge
                      className={styles.precAge}
                      ms={now - Date.parse(c.updated_at)}
                      band={waitBand(now - Date.parse(c.updated_at), c.status, bands)}
                      note={bandNote(waitBand(now - Date.parse(c.updated_at), c.status, bands), bands, 'wait')}
                    />
                  </div>
                ))}
                {queued > ahead.length && (
                  <p className={styles.none}>+{queued - ahead.length} more in flight.</p>
                )}
              </div>
            )}

            {verb === null && !wide && (
              <>
                <div className={styles.stageHead}>
                  <Eyebrow>Settled</Eyebrow>
                  <span className={styles.stageVerb}>{robot.robot_id}</span>
                </div>
                {settledHere.length === 0 ? (
                  <p className={styles.none}>Nothing settled on this robot in the window.</p>
                ) : (
                  <div className={styles.ahead} data-stale={!feed.live}>
                    {settledHere.map((c) => (
                      <div key={c.id} className={styles.aheadRow} data-tone={CMD_TONE[c.status]}>
                        <StatusDot tone={CMD_TONE[c.status]} />
                        <span className={styles.precId}>#{c.id}</span>
                        <span className={styles.aheadVerb}>{c.command}</span>
                        <span className={styles.precState}>{c.status}</span>
                        <span className={styles.precAge}>{relative(c.updated_at, now)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* ---- HOW EACH VERB HAS GONE HERE --------------------------
                    The bench's idle state used to stop ~200px above the pinned
                    pre-flight — a rail whose bottom third was ground. This is
                    what the reference rails spend that space on (anduril's
                    ASSET DATA block), and here it is the one aggregate the
                    operator is about to act on: pick a verb, and this is its
                    record on this machine. A row stages its verb, which is the
                    same act as pressing its number — nothing is sent. */}
                {verbLedger.length > 0 && (
                  <>
                    <div className={styles.stageHead}>
                      <Eyebrow>Per verb</Eyebrow>
                      <span className={styles.stageVerb}>
                        {verbLedger.filter((l) => l.n > 0).length}/{verbLedger.length} used
                      </span>
                    </div>
                    <div className={styles.verbLedger} data-stale={!feed.live}>
                      {verbLedger.filter((l) => l.last !== null).map((l) => (
                        <button
                          key={l.name}
                          type="button"
                          className={styles.vlRow}
                          data-tone={l.last === null ? 'neutral' : CMD_TONE[l.last.status]}
                          data-idle={l.n === 0}
                          disabled={l.verb === null}
                          title={
                            l.verb === null
                              ? `${l.name} is in this robot's history but not in its published profile — it cannot be staged`
                              : l.last === null
                                ? `${l.name} has never been issued to ${robot.robot_id} in this window. Stage it.`
                                : `${l.n} run${l.n === 1 ? '' : 's'} on ${robot.robot_id}; the last, #${l.last.id}, ${l.last.status}. Stage ${l.name}.`
                          }
                          onClick={() => {
                            if (l.verb !== null) stage(l.verb);
                          }}
                        >
                          <span className={styles.vlVerb}>{l.name}</span>
                          {l.verb === null && <span className={styles.vlOff}>off-profile</span>}
                          <span className={styles.vlN}>{l.n}</span>
                          <span className={styles.vlState}>
                            <StatusDot tone={CMD_TONE[l.last?.status ?? 'pending']} />
                            {l.last?.status}
                          </span>
                          <span className={styles.vlAge}>
                            {l.last === null ? NO_VALUE : relative(l.last.updated_at, now)}
                          </span>
                        </button>
                      ))}
                      {/* THE UNEXERCISED HALF OF THE PROFILE, IN ONE LINE.
                          `never issued` is a reading and it belongs on screen —
                          it is why the next press would be the first — but six
                          rows of it is six rows of the same word. Named
                          together, it is one fact and one line. */}
                      {verbLedger.some((l) => l.last === null) && (
                        <p className={styles.vlNever}>
                          <span className={styles.vlNeverWord}>never issued here</span>
                          <span className={styles.vlNeverList}>
                            {verbLedger.filter((l) => l.last === null).map((l) => l.name).join(' · ')}
                          </span>
                        </p>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ---- THE MACHINE ITSELF ------------------------------------
                WHICH BUILD AM I ABOUT TO SEND THIS TO?

                THE BAR §6 lists provenance as one of the four facts that must
                be on screen without asking — "which robot, which agent
                version, which morphology" — and §13 tell 23 asks whether the
                version is on the row at fleet scale. On a write surface it is
                worth more than anywhere else: a `climb` that fails on 2.9.4
                and succeeds on 3.0.0-rc2 is a fleet fact, and the operator
                standing at the commit is the person who needs it.

                It also closes the bench's one dead space. At rest, on a robot
                with a short ledger, the pane's content stopped ~220px above
                the pinned pre-flight, and the reference rails (anduril's ASSET
                DATA block) spend exactly that space on exactly this. Every row
                is off `/api/robots/{id}`; a field the registry does not carry
                renders as the em dash. */}
            <div className={styles.stageHead}>
              <Eyebrow>Machine</Eyebrow>
              <span className={styles.stageVerb}>{robot.robot_id}</span>
            </div>
            <KeyValue layout="rows" items={machine} />
        </div>

        {issue.isError && <ErrorLine>{String(issue.error)}</ErrorLine>}
      </>
    );
  }

  return (
    <Panel
      className={className === undefined ? styles.panel : `${styles.panel} ${className}`}
      title={title}
      meta={meta}
      pad="none"
      scroll={false}
    >
      {/* ---- TARGET, ABOVE EVERYTHING ------------------------------------
          Where a command is going is the most consequential field on this
          pane, and it used to sit in the 28px panel header as a `tools`
          control — beside a liveness tag, under the title, at the size of a
          settings affordance. It is not a settings affordance. It leads the
          pane, on its own line, in the same `EYEBROW  value` shape the blocks
          below it use, and it is present in every state of the pane including
          the one where no robot is picked yet. */}
      <div className={styles.stack}>
      {targetControl !== undefined && (
        <div className={styles.targetLine}>
          <Eyebrow>Target</Eyebrow>
          {targetControl}
        </div>
      )}
      <div className={styles.frame} ref={frameRef}>
        <div className={styles.pane}>
          <div
            className={styles.scroll}
            ref={(n) => { paneRef(n); paneMeterRef(n); }}
            data-edge={paneEdge}
          >
            {body}
          </div>
          {/* Overlay scrollbars paint nothing at rest, so the pane draws its
              own: 3px, always there while there is more, positioned in the
              content. A fade at the edge says "something is cut"; this says
              how much and where. */}
          {paneView.scrollable && (
            <div className={styles.scrollMeter} aria-hidden="true">
              <span
                className={styles.scrollThumb}
                style={{ top: `${paneView.top * 100}%`, height: `${paneView.size * 100}%` }}
              />
            </div>
          )}
        </div>

        {/* ---- the commit, pinned to the rail's bottom edge -----------------
            It never scrolls out of reach, its two halves are the same size,
            and the arming switch sits on its own row above them — a control
            that can lose work is never on the commit's row. */}
        <div className={styles.commit}>
          {/* ---- PRE-FLIGHT, pinned ------------------------------------------
              The robot's live condition is a qualification OF THE COMMIT, so
              it lives with the commit and not above it in a pane that
              scrolls. It used to sit in the middle with the parameters and
              the precedent, which meant that on a 700px rail the last thing
              an operator saw before pressing Send was a row of half-cut
              glyphs where the battery reading had been — the one fact that
              should never be one scroll away from the button that moves a
              machine. Pinned, it holds its place through every state of the
              pane, exactly the way the safety bar does in THE BAR §11.7.

              It also says how old it is. A pre-flight is only as good as the
              read behind it: when the read has stopped, the header states the
              last good time in words, because the checks below are then the
              robot's last known condition and not its condition now. */}
          {/* Not gated on the profile: the standing condition of a machine that
              cannot be dispatched to is exactly what tells the operator WHY —
              `link provisioning · never seen` is the whole answer for a robot
              that has not published its verb set. */}
          {robot !== undefined && (
            <div className={styles.preflight}>
              <div className={styles.stageHead}>
                <Eyebrow>Pre-flight</Eyebrow>
                {feed.live ? (
                  <span className={styles.stageVerb}>{robot.robot_id}</span>
                ) : (
                  <span className={styles.stageStale}>
                    as of {utcTime(new Date(feed.lastGoodAt ?? clock).toISOString())} ·{' '}
                    {duration(feed.ageMs ?? 0)} old
                  </span>
                )}
              </div>
              {/* Idle it is one line — the robot's standing condition, read at
                  a glance before a verb is even picked. Staged it opens to the
                  full block, with the note on each check, because that is the
                  moment the operator is judging whether to move a machine. */}
              <div className={styles.checks} data-stale={!feed.live} data-compact={verb === null || tight}>
                {checks.map((c) => (
                  <div key={c.key} className={styles.check} data-tone={c.tone}>
                    <StatusDot tone={c.tone} hollow={c.tone === 'offline'} />
                    <span className={styles.checkLabel}>{c.label}</span>
                    <span className={styles.checkValue}>{c.value}</span>
                    {c.note !== null && <span className={styles.checkNote}>{c.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* The consequence sentence gets its own line and is allowed to
              wrap. Clipped to `commands motion — the robot wil…` it is
              decoration; a statement of what the machine is about to do has
              to be readable in full, in a 328px rail, before the commit. */}
          <div className={styles.consequence} data-tone={verb === null ? 'neutral' : hazardTone(verb.hazard)}>
            {verb !== null && (
              <div className={styles.consTop}>
                <span className={styles.consClass}>{verb.hazard}</span>
                <span className={styles.consSpacer} />
                {gated && lease === undefined && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={armed}
                    className={styles.arm}
                    title={
                      armed
                        ? 'Armed. Click to stand the class back down.'
                        : `Arm the ${verb.hazard} class before this can be sent.`
                    }
                    onClick={() => setArmed((a) => !a)}
                  >
                    <Icon name={armed ? 'unlock' : 'lock'} size={12} />
                    <span className={styles.armWord}>{armed ? 'Armed' : 'Arm'}</span>
                    <span className={styles.armTrack}><span className={styles.armKnob} /></span>
                  </button>
                )}
                {/* The page's lease, echoed. Not a second switch to take it —
                    the switch lives in the identity row and is on screen on
                    every tab — but the state it is in, its remaining time, and
                    a one-click release, so authority is never something the
                    operator has to travel to read. */}
                {gated && lease !== undefined && (
                  lease.held ? (
                    <button
                      type="button"
                      className={styles.leaseEcho}
                      data-held="true"
                      title={`Actuation authority held${
                        lease.msLeft === null ? '' : ` — releases in ${duration(lease.msLeft)}`
                      }. Click to release it now.`}
                      onClick={lease.onRelease}
                    >
                      <Icon name="unlock" size={12} />
                      <span className={styles.armWord}>
                        lease held{lease.msLeft === null ? '' : ` · ${duration(lease.msLeft)}`}
                      </span>
                      <span className={styles.leaseRelease}>release</span>
                    </button>
                  ) : (
                    /* NOT A LABEL SAYING WHAT IS MISSING — the control that
                       fixes it. It takes the embedding page's own lease, so
                       this is one authority rendered at the point of action
                       and not a second one to arm. */
                    <button
                      type="button"
                      role="switch"
                      aria-checked={false}
                      className={styles.leaseEcho}
                      data-held="false"
                      title={`This verb is ${verb.hazard} class. Take actuation authority — the same lease ${lease.where} takes.`}
                      onClick={lease.onAcquire}
                    >
                      <Icon name="lock" size={12} />
                      <span className={styles.armWord}>lease not held</span>
                      <span className={styles.leaseRelease}>unlock</span>
                    </button>
                  )
                )}
              </div>
            )}
            <span className={styles.consText}>
              {verb === null
                ? profile === null || robotId == null
                  ? 'Nothing to dispatch.'
                  : 'Pick a verb, or press its number.'
                : hazardConsequence(verb.hazard)}
            </span>
          </div>
          <div className={styles.pair}>
            <Button
              size="sm"
              variant="secondary"
              block
              disabled={verb === null}
              kbd="Esc"
              data-kbd="commands-cancel"
              onClick={standDown}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              /* The accent is spent only once the command can actually leave:
                 a permanently filled button on an idle rail is a primary verb
                 that means nothing, and a filled one over a `cut` that has not
                 been armed is worse — it looks live. The fill arriving IS the
                 signal that the pre-flight passed. */
              variant={ready ? 'primary' : 'secondary'}
              block
              disabled={!ready}
              kbd="⌘⏎"
              /* The key sheet's proof for ⌘⏎, and where it stamps
                 `aria-keyshortcuts`. */
              data-kbd="commands-send"
              title={blockedBecause === null ? `Send ${verb?.name} to ${robotId}` : `Cannot send — ${blockedBecause}`}
              onClick={send}
            >
              Send
            </Button>
          </div>
          {/* The exact payload about to leave, or the one reason it cannot.
              With nothing staged there is neither, and the line above already
              says so — a second row reading `no verb staged` under
              `Pick a verb, or press its number` is the same sentence twice. */}
          {/* NOTHING STAGED, BUT SOMETHING WAS JUST SENT. The commit line's slot
              holds the receipt instead of standing empty: the id the platform
              gave it, where it went, the state it is in now and how long it
              has been in it, ticking off the same read the queue above uses.
              It is the answer to "did that send", printed where the press
              happened rather than on a board 400px away. */}
          {verb === null && receiptRow !== null && (
            <div className={styles.commitLine} data-receipt="true">
              <StatusDot tone={CMD_TONE[receiptRow.status]} />
              <span className={styles.commitVerb}>#{receiptRow.id}</span>
              <span className={styles.commitRobot}>{receiptRow.command}</span>
              <span
                className={styles.receiptState}
                data-tone={CMD_TONE[receiptRow.status]}
                title={CMD_HINT[receiptRow.status]}
              >
                {receiptRow.status}
              </span>
              <span className={styles.receiptAge}>
                {duration(Math.max(0, now - Date.parse(receiptRow.updated_at)))} in state
              </span>
            </div>
          )}
          {verb !== null && (
            <div className={styles.commitLine}>
              {blockedBecause === null ? (
                <>
                  <span className={styles.commitVerb}>{verb.name}</span>
                  <span className={styles.commitArrow}>→</span>
                  <span className={styles.commitRobot}>{robotId}</span>
                  {rows.length > 0 && (
                    <span className={styles.commitParams}>{previewParams(rows, drafts)}</span>
                  )}
                  {/* SAID IN WORDS, NOT INFERRED FROM THE CHROME. The whole
                      contract of this panel is that a verb click has changed
                      nothing on any robot; the line that names the payload is
                      where that belongs, in the same wording the queue's own
                      record pane uses when it re-stages a command. */}
                  <span className={styles.commitState}>staged, not sent</span>
                </>
              ) : (
                <span className={styles.commitWhy}>{blockedBecause}</span>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </Panel>
  );
}

/** The exact payload about to leave, in the format the ledger will show it. */

/* --------------------------------------------------------------------------
   WHERE THE VIEWPORT SITS IN THE CONTENT, as two 0–1 fractions.

   This console runs under overlay scrollbars: a scroll box measures
   `offsetWidth - clientWidth === 0` and the native thumb is painted only
   while the wheel is turning. So "how much of this is off screen, and where
   am I in it" has no persistent native answer, and a pane whose only clipping
   signal is a fade reads as a broken render rather than as "scroll". These
   two numbers drive a drawn 3px track that is always there.
   -------------------------------------------------------------------------- */

interface ScrollView {
  /** Fraction of the content above the viewport's top edge. */
  top: number;
  /** Fraction of the content the viewport covers. 1 = nothing is hidden. */
  size: number;
  scrollable: boolean;
}

const NOT_SCROLLABLE: ScrollView = { top: 0, size: 1, scrollable: false };

function useScrollView<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  view: ScrollView;
} {
  const [node, setNode] = useState<T | null>(null);
  const [view, setView] = useState<ScrollView>(NOT_SCROLLABLE);
  const ref = useCallback((n: T | null) => setNode(n), []);

  useEffect(() => {
    if (node === null) {
      setView(NOT_SCROLLABLE);
      return undefined;
    }
    const measure = () => {
      const total = node.scrollHeight;
      const seen = node.clientHeight;
      setView(
        total <= seen + 1 || total === 0
          ? NOT_SCROLLABLE
          : { top: node.scrollTop / total, size: seen / total, scrollable: true },
      );
    };
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    const mo = new MutationObserver(measure);
    mo.observe(node, { childList: true, subtree: true });
    return () => {
      node.removeEventListener('scroll', measure);
      ro.disconnect();
      mo.disconnect();
    };
  }, [node]);

  return { ref, view };
}

function previewParams(rows: readonly ParamRow[], drafts: Record<string, Draft>): string {
  const out: Record<string, unknown> = {};
  for (const r of rows) {
    const d = drafts[r.name];
    if (d === undefined) continue;
    const c = coerce(r.spec, d);
    if (c.ok) out[r.name] = c.value;
  }
  return paramDigest(out);
}

/* --------------------------------------------------------------------------
   One typed parameter. Never a JSON textarea: a number is a number field with
   its unit and its range, a coordinate is two number fields, a boolean is a
   select with both words in it.
   -------------------------------------------------------------------------- */

function ParamField({
  row,
  value,
  error,
  onChange,
}: {
  row: ParamRow;
  value: Draft;
  error: string | null;
  onChange: (next: Draft) => void;
}) {
  const { name, spec } = row;
  const id = `param-${name}`;

  let control: ReactNode;
  if (spec.kind === 'latlon') {
    const v = value as { lat: string; lon: string };
    control = (
      <span className={styles.latlon}>
        <Input
          size="sm"
          mono
          id={id}
          inputMode="decimal"
          aria-label={`${name} latitude`}
          placeholder="lat"
          invalid={error !== null}
          value={v.lat}
          onChange={(e) => onChange({ ...v, lat: e.currentTarget.value })}
        />
        <Input
          size="sm"
          mono
          inputMode="decimal"
          aria-label={`${name} longitude`}
          placeholder="lon"
          invalid={error !== null}
          value={v.lon}
          onChange={(e) => onChange({ ...v, lon: e.currentTarget.value })}
        />
      </span>
    );
  } else if (spec.kind === 'bool') {
    control = (
      <Select
        size="sm"
        mono
        id={id}
        value={value === true ? 'true' : 'false'}
        onChange={(e) => onChange(e.currentTarget.value === 'true')}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </Select>
    );
  } else {
    control = (
      <Input
        size="sm"
        mono
        id={id}
        invalid={error !== null}
        {...(spec.kind === 'number' ? { inputMode: 'decimal' as const } : {})}
        {...(spec.placeholder === undefined ? {} : { placeholder: spec.placeholder })}
        value={String(value)}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    );
  }

  const limits =
    spec.min !== undefined && spec.max !== undefined
      ? `${spec.min}–${spec.max}`
      : spec.min !== undefined
        ? `≥ ${spec.min}`
        : null;

  /* Range, provenance and the honest "we have no published type for this"
     note, in one line that ellipsises rather than squeezing the field's name.
     The full string stays on the title. */
  const hint = [limits, row.seed, spec.source === 'unknown' ? 'type not published' : null]
    .filter((v): v is string => v !== null)
    .join(' · ');

  /* WHERE THE TYPE AND THE RANGE CAME FROM, said out loud on hover.
     `/api/command-profiles` publishes a verb's required parameter NAMES and
     nothing else — no type, no unit, no range — so a range printed under a
     field is the console's own table or an inference off this robot's
     history, never the platform's word. An operator judging whether 4 m/s is
     really the limit is entitled to know which. */
  const provenance =
    spec.source === 'contract'
      ? "unit and range from the console's own parameter table — the profile endpoint publishes names only"
      : spec.source === 'observed'
        ? 'type inferred from a value this parameter has actually carried on a real command'
        : 'no published type and nothing observed — sent as a string';
  const hintTitle = hint === '' ? provenance : `${hint} — ${provenance}`;

  return (
    <div className={styles.param} data-invalid={error !== null}>
      <label className={styles.paramName} htmlFor={id}>
        {name}
        {/* Required is the default and carries no mark; the exception is
            annotated. An accent asterisk on every required field would give
            the console's one accent hue a fourth job. */}
        {!row.required && <span className={styles.opt}>(optional)</span>}
      </label>
      <div className={styles.paramControl}>{control}</div>
      <div className={styles.paramMeta}>
        {spec.unit !== undefined && <span className={styles.unit}>{spec.unit}</span>}
        {error !== null ? (
          <span className={styles.paramError}>{error}</span>
        ) : (
          <span className={styles.paramHint} title={hintTitle}>
            {hint}
          </span>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   THE ROBOT'S COMMAND SURFACE — one robot's slice of the C2 board.

   This is the whole `Commands` tab of a robot's detail page, and it is
   deliberately the same three ideas the fleet queue (/commands) is built from,
   in the same order, with the same renderers:

     HISTORY   the robot's own command ledger, grouped by lifecycle state, with
               a per-row state edge, the four-slot STAGE track, issue→state
               LATENCY and time IN STATE — the /commands column set minus the
               robot column, which the page already names. A row expands IN
               PLACE (never a modal, THE BAR §8 tell 12) into its real
               transition trace, its params and its result.

     ACTIVITY  the robot's slice of `/api/audit/commands` at true timestamps,
               newest first — the same log the fleet board runs underneath its
               queue, filtered to this machine.

     DISPATCH  the identical staged composer the queue's rail carries. One
               concept, one control: pick a verb (or press its number), edit
               its parameters as typed fields, read the precedent and the
               pre-flight, then commit with the pinned Cancel / Send pair.
               Nothing reaches the robot on a single click, here or there.

   Each panel answers its OWN freshness question, because they read different
   endpoints at different cadences and one light for several channels is a
   defect (§13 tell 27). Every age drawn from a row is measured against that
   panel's last good read, so a dead poll cannot keep a counter ticking.
   ========================================================================== */

/** How many audit events the activity log keeps for one robot. */
const ROBOT_FEED_CAP = 120;

interface HistGroup {
  status: CommandStatus;
  rows: readonly LedgerCommand[];
  /* TWO DIFFERENT QUESTIONS, BOTH NAMED (THE BAR §13 tell 24), exactly as the
     fleet board asks them. While the group still holds unsettled work the age
     is how long the oldest of it has been WAITING, inked against the ladder —
     so a collapsed `acked 1` still shows it is rotting. Once every row has
     settled nothing is waiting, and the number is how long ago the group last
     moved: history, and dim. */
  waiting: boolean;
  age: number;
  band: Band | null;
}

export function RobotCommandsTab({
  robotId,
  className,
  lease,
}: {
  robotId: string;
  className?: string | undefined;
  lease?: LeaseState | undefined;
}) {
  const historyQ = useRobotCommands(robotId);
  const auditQ = useCommandAudit();
  const { connected } = useCommandStream(robotId);
  const clock = useNow();

  const histH = health(
    [source(`/api/robots/${robotId}/commands`, 20_000, historyQ)],
    clock,
  );
  const feedH = health([source('/api/audit/commands', 3_000, auditQ)], clock);
  const histRead = useRead(historyQ, {
    path: `/api/robots/${robotId}/commands`,
    everyMs: 20_000,
    subject: 'the command history',
  });
  const auditRead = useRead(auditQ, {
    path: '/api/audit/commands',
    everyMs: 3_000,
    subject: 'the command ledger',
  });
  const histNow = histH.asOf;
  const feedNow = feedH.asOf;

  const [openId, setOpenId] = useState<number | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set<string>());
  /* Which verb the composer has staged, mirrored here only so the rail can
     give it the height a staged command needs — parameters, precedent and a
     pre-flight do not fit under a verb ledger, and a safety block an operator
     has to scroll to find is a safety block they will not read. */
  const [stagedVerb, setStagedVerb] = useState<string | null>(null);

  /* The published profile, read once here and handed down: the dispatch pane
     below reads the same row off the same query key, so this costs no
     request. A robot that has never enrolled carries no profile at all. */
  const robotQ = useRobot(robotId);
  const profile = useMemo(
    () => readProfile(robotQ.data?.command_profile),
    [robotQ.data?.command_profile],
  );

  /* This robot's audit events, indexed by command. The ledger row the queue
     folds from the fleet-wide log is the same row here — the robot's own
     endpoint is authoritative for status, and the log supplies the real
     transitions the trace needs. Neither is derived from the other. */
  const eventsByCmd = useMemo(() => {
    const m = new Map<number, CommandEvent[]>();
    for (const e of auditQ.data ?? []) {
      if (e.robot_id !== robotId) continue;
      const list = m.get(e.command_id);
      if (list === undefined) m.set(e.command_id, [e]);
      else list.push(e);
    }
    for (const list of m.values()) {
      list.sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts) || a.id - b.id);
    }
    return m;
  }, [auditQ.data, robotId]);

  const rows = useMemo<LedgerCommand[]>(
    () =>
      (historyQ.data ?? []).map((c: Command) => ({
        id: c.id,
        robot_id: c.robot_id,
        command: c.command,
        params: c.params,
        status: c.status,
        issued_by: c.issued_by,
        issued_at: c.issued_at,
        updated_at: c.updated_at,
        result: c.result,
        events: eventsByCmd.get(c.id) ?? [],
        live: IN_FLIGHT.has(c.status),
      })),
    [historyQ.data, eventsByCmd],
  );

  /* ONE LADDER FOR THE WHOLE TAB. Measured over this robot's own settled
     commands and then handed to every duration on the surface — the ledger's
     `Latency` and `In state` cells, these group headers, and the dispatch
     pane's standing queue — so nothing here can disagree with anything else
     here about whether a wait is slow or stuck. `ladderOf` says out loud when
     the sample is too thin to measure and falls back to fixed edges. */
  const ladder = useMemo(() => {
    const samples: number[] = [];
    for (const c of rows) {
      const ms = latencyMs(c);
      if (ms !== null) samples.push(ms);
    }
    samples.sort((a, b) => a - b);
    return ladderOf(samples);
  }, [rows]);

  const groups = useMemo<HistGroup[]>(
    () =>
      CMD_STATUSES.map((status) => {
        const list = rows.filter((c) => c.status === status);
        let oldestWait: number | null = null;
        let oldestAny = 0;
        const bands: (Band | null)[] = [];
        for (const c of list) {
          const age = histNow - Date.parse(c.updated_at);
          if (age > oldestAny) oldestAny = age;
          if (IN_FLIGHT.has(c.status)) {
            if (oldestWait === null || age > oldestWait) oldestWait = age;
            bands.push(waitBand(age, c.status, ladder));
          }
        }
        const waiting = oldestWait !== null;
        return {
          status,
          rows: list,
          waiting,
          age: oldestWait ?? oldestAny,
          band: waiting ? worstBand(bands) : null,
        };
      }).filter((g) => g.rows.length > 0),
    [rows, histNow, ladder],
  );

  const feed = useMemo(() => {
    const verbOf = new Map(rows.map((c) => [c.id, c.command]));
    return (auditQ.data ?? [])
      .filter((e) => e.robot_id === robotId)
      .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts) || b.id - a.id)
      .slice(0, ROBOT_FEED_CAP)
      .map((ev) => ({ ev, verb: verbOf.get(ev.command_id) ?? '' }));
  }, [auditQ.data, robotId, rows]);

  const inFlight = rows.filter((c) => IN_FLIGHT.has(c.status)).length;
  const failed = rows.filter((c) => c.status === 'failed').length;
  const hasLedger = (profile?.length ?? 0) > 0 || rows.length > 0;

  /* The ledger is one tab stop, not thirty: the open row takes it, else the
     first row drawn, so the ledger is always reachable and the arrows take
     over from there. */
  const visible = groups.filter((g) => !closed.has(g.status)).flatMap((g) => g.rows);
  const tabStop =
    openId !== null && visible.some((c) => c.id === openId) ? openId : visible[0]?.id ?? -1;

  const toggleGroup = useCallback((key: string) => {
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /* The ledger is one tab stop; the arrows walk it, like every other list in
     the console. */
  const onHistKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const list = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-cmd]')];
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (i === -1) return;
    e.preventDefault();
    const next = list[e.key === 'ArrowDown' ? Math.min(list.length - 1, i + 1) : Math.max(0, i - 1)];
    next?.focus();
  }, []);

  const onFeedKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const list = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-ev]')];
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (i === -1) return;
    e.preventDefault();
    const next = list[e.key === 'ArrowDown' ? Math.min(list.length - 1, i + 1) : Math.max(0, i - 1)];
    next?.focus();
  }, []);

  /* The host exists to BE the container: a container query cannot restyle its
     own container, so the element that carries `container-type` has to sit
     outside the grid it governs. */
  /* THE LEDGER TAKES ITS ROWS AND STOPS.
     `/api/robots/{id}/commands` returns a handful of rows for every robot in
     the fleet, so a ledger pane sized as a fixed fraction of the column spent
     a third of its height on empty surface under four rows — the exact
     failure the robot page's own doctrine names ("a 26px header over 445px of
     empty panel body reads as a broken instrument"). It is measured instead:
     its own header, its column head and one row per row and per group header,
     capped at 62% of the column so a robot with a long history still leaves
     the log a pane to live in. The surplus goes to Activity, which is a live
     tail and is the one pane here that SHOULD have room to grow into.
     Nothing to show at all and both panes hug their one honest line. */
  const histPx = 28 + 20 + rows.length * 26 + groups.length * 20 + 2;

  return (
    <div className={className === undefined ? styles.tabHost : `${styles.tabHost} ${className}`}>
    <div
      className={styles.tab}
      data-empty={rows.length === 0 && feed.length === 0}
      style={{ '--hist-h': `${histPx}px` } as CSSProperties}
    >
      <Panel
        className={styles.hist}
        title="Command history"
        count={rows.length}
        meta={
          <>
            <span className={styles.tabMeta} title="Commands on this robot that have not settled">
              {inFlight} open
            </span>
            {failed > 0 && (
              <span className={styles.tabFail} title="Commands in the window that ended in failure">
                {failed} failed
              </span>
            )}
            <span
              className={styles.tabMeta}
              data-tone={histH.live ? undefined : 'warn'}
              title={
                connected
                  ? 'Transitions arrive over MQTT as they happen'
                  : 'MQTT is unavailable here — the ledger re-reads every 20s instead'
              }
            >
              {connected ? 'push' : 'poll 20s'}
            </span>
            <FeedTag health={histH} now={clock} pulse={connected} read={histRead} />
          </>
        }
        loading={historyQ.isLoading && rows.length === 0}
        pad="none"
        scroll={false}
      >
        <div className={styles.histFrame}>
          {!histH.live && <StaleLine health={histH} now={clock} read={histRead} />}
          <div
            className={styles.histScroll}
            data-stale={!histH.live}
            onKeyDown={onHistKey}
          >
            <div className={styles.histHead} role="row">
              <span />
              <span className={styles.right}>#</span>
              <span>Verb</span>
              <span title="issue → sent → ack → settle. A hatched slot is one the platform recorded no transition through.">
                Stage
              </span>
              <span>Detail</span>
              <span className={styles.optBy}>By</span>
              <span className={`${styles.right} ${styles.optIssued}`} title="When the command was issued, UTC">
                Issued
              </span>
              <span
                className={`${styles.right} ${styles.optLat}`}
                title="Issue to the state the command is in now"
              >
                Latency
              </span>
              <span className={styles.right} title="Time since the command entered its current state">
                In state
              </span>
            </div>

            {rows.length === 0 ? (
              <Empty>
                {historyQ.isLoading ? 'Reading the ledger…' : `No commands issued to ${robotId}.`}
              </Empty>
            ) : (
              groups.map((g) => {
                const shut = closed.has(g.status);
                return (
                  <div key={g.status} className={styles.histGroup}>
                    <button
                      type="button"
                      className={styles.histGroupHead}
                      aria-expanded={!shut}
                      data-tone={CMD_TONE[g.status]}
                      title={CMD_HINT[g.status]}
                      onClick={() => toggleGroup(g.status)}
                    >
                      <Icon name={shut ? 'chevron-right' : 'chevron-down'} size={12} />
                      <span className={styles.histGroupWord}>{g.status}</span>
                      <span className={styles.histGroupCount}>{g.rows.length}</span>
                      <span className={styles.histSpacer} />
                      <span className={styles.histGroupAge}>
                        <span
                          className={styles.histGroupAgeWord}
                          title={
                            g.waiting
                              ? 'How long the oldest command in this group that has not settled has been waiting'
                              : 'Every command in this group has settled — how long ago the most recent one did'
                          }
                        >
                          {g.waiting ? 'waiting' : 'settled'}
                        </span>
                        <BandedAge
                          ms={g.age}
                          band={g.band}
                          note={bandNote(g.band, ladder, 'wait')}
                        />
                      </span>
                    </button>
                    {!shut &&
                      g.rows.map((c) => (
                        <HistoryRow
                          key={c.id}
                          cmd={c}
                          now={histNow}
                          ladder={ladder}
                          open={c.id === openId}
                          /* One tab stop for the whole ledger; the arrows walk
                             it from there, as on every other list here. */
                          tabbable={c.id === tabStop}
                          onToggle={() => setOpenId((p) => (p === c.id ? null : c.id))}
                        />
                      ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Panel>

      <Panel
        className={styles.act}
        title="Activity"
        count={feed.length}
        meta={
          <>
            <span
              className={styles.tabMeta}
              title={`This robot's slice of /api/audit/commands, newest first. The board reads the newest ${AUDIT_WINDOW} events fleet-wide.`}
            >
              audit
            </span>
            <FeedTag health={feedH} now={clock} pulse={connected} read={auditRead} />
          </>
        }
        pad="none"
        scroll={false}
      >
        <div className={styles.actFrame}>
          {!feedH.live && <StaleLine health={feedH} now={clock} read={auditRead} />}
          <div className={styles.actHead} role="row">
            <span>Time</span>
            <span className={styles.right}>Age</span>
            <span className={styles.right}>#</span>
            <span>Event</span>
            <span>Verb · reason</span>
            <span className={styles.right}>By</span>
          </div>
          <div className={styles.actScroll} data-stale={!feedH.live} onKeyDown={onFeedKey}>
            {feed.length === 0 ? (
              <Empty>No lifecycle events for {robotId} in the audit window.</Empty>
            ) : (
              feed.map(({ ev, verb }, i) => (
                <ActivityRow
                  key={ev.id}
                  ev={ev}
                  verb={verb}
                  now={feedNow}
                  selected={ev.command_id === openId}
                  tabbable={i === 0}
                  onPick={() => setOpenId(ev.command_id)}
                />
              ))
            )}
          </div>
        </div>
      </Panel>

      {/* The right rail is the same two-pane rail the fleet queue runs: a
          dispatch control sized by exactly what it is, and under it the pane
          that absorbs whatever height is left. There the second pane is the
          selected command; here it is this robot's own verb ledger, which is
          the question the operator has at the moment of dispatch — has this
          machine ever accepted this verb, and how did it end. */}
      <div className={styles.tabRail} data-solo={!hasLedger} data-staged={stagedVerb !== null}>
        <CommandPanel
          className={styles.tabDispatch}
          robotId={robotId}
          ladder={ladder}
          onIssued={(id) => setOpenId(id)}
          onStagedChange={setStagedVerb}
          {...(lease === undefined ? {} : { lease })}
        />
        {/* A robot that has published nothing and been sent nothing has no
            ledger to draw, and a pane whose whole content is one line of
            "nothing here" has no business taking 600px of the rail. The
            dispatch control — which is already saying, in the same words,
            that the profile has not arrived — takes the height instead. */}
        {hasLedger && (
          <VerbLedger
            className={styles.tabLedger}
            robotId={robotId}
            profile={profile}
            rows={rows}
            now={histNow}
            health={histH}
            read={histRead}
            clock={clock}
            openId={openId}
            onPick={setOpenId}
          />
        )}
      </div>
    </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   VERB LEDGER — the robot's published profile crossed with what has actually
   been sent to it.

   Every verb the robot advertises, in the order the dispatch list above draws
   them, with how many times it has been issued in the ledger window and how
   the last one ended. A verb that has never been sent says so; a verb that
   appears in the history but not in the profile is called out, because that
   is a profile that changed under the fleet and the operator should see it
   rather than have the row quietly dropped.
   -------------------------------------------------------------------------- */

interface LedgerLine {
  name: string;
  hazard: string | null;
  count: number;
  last: LedgerCommand | null;
}

function VerbLedger({
  className,
  robotId,
  profile,
  rows,
  now,
  health: h,
  read,
  clock,
  openId,
  onPick,
}: {
  className: string;
  robotId: string;
  /** The robot's published profile, or null while it has published none. */
  profile: ProfileVerb[] | null;
  rows: readonly LedgerCommand[];
  now: number;
  health: Health;
  /** The read the rows came from — `N used` is counted from it. */
  read: Read;
  clock: number;
  openId: number | null;
  onPick: (id: number) => void;
}) {
  const { ref: scrollRef, edge } = useScrollEdges<HTMLDivElement>();

  const lines = useMemo<LedgerLine[]>(() => {
    const byVerb = new Map<string, LedgerCommand[]>();
    for (const c of rows) {
      const list = byVerb.get(c.command);
      if (list === undefined) byVerb.set(c.command, [c]);
      else list.push(c);
    }
    const pick = (name: string): LedgerCommand | null =>
      (byVerb.get(name) ?? []).reduce<LedgerCommand | null>(
        (acc, c) => (acc === null || Date.parse(c.issued_at) > Date.parse(acc.issued_at) ? c : acc),
        null,
      );

    const out: LedgerLine[] = (profile ?? []).map((v) => ({
      name: v.name,
      hazard: v.hazard,
      count: byVerb.get(v.name)?.length ?? 0,
      last: pick(v.name),
    }));
    const known = new Set(out.map((l) => l.name));
    for (const name of byVerb.keys()) {
      if (known.has(name) || name === '') continue;
      out.push({ name, hazard: null, count: byVerb.get(name)?.length ?? 0, last: pick(name) });
    }
    /* Most recently exercised first, then everything the robot advertises and
       nobody has sent it. The pane is a record, and a record that opens on
       four rows of `never issued` has buried the only rows that carry one.
       The profile's own order is still on screen — it is the verb list
       directly above this. */
    out.sort((a, b) => {
      if (a.last === null || b.last === null) {
        return (a.last === null ? 1 : 0) - (b.last === null ? 1 : 0) || a.name.localeCompare(b.name);
      }
      return Date.parse(b.last.updated_at) - Date.parse(a.last.updated_at);
    });
    return out;
  }, [rows, profile]);

  const used = lines.filter((l) => l.count > 0).length;

  return (
    <Panel
      className={className}
      title="Verb ledger"
      count={lines.length}
      meta={
        <>
          <span className={styles.tabMeta} title="Verbs this robot has actually been sent in the window">
            <Figure of={read} value={used} /> used
          </span>
          <FeedTag health={h} now={clock} pulse={false} read={read} />
        </>
      }
      pad="none"
      scroll={false}
    >
      <div className={styles.ledgerFrame}>
        <div className={styles.ledgerHead} role="row">
          <span />
          <span>Verb</span>
          <span className={styles.right} title="Times issued to this robot in the ledger window">
            N
          </span>
          <span>Last</span>
          <span className={styles.right} title="Since the last one entered its current state">
            Age
          </span>
        </div>
        <div className={styles.ledgerScroll} ref={scrollRef} data-edge={edge} data-stale={!h.live}>
          {lines.length === 0 ? (
            <Empty>
              {profile === null ? `${robotId} has published no command profile.` : 'No verbs.'}
            </Empty>
          ) : (
            lines.map((l) => {
              const tone = l.last === null ? null : CMD_TONE[l.last.status];
              return (
                <button
                  key={l.name}
                  type="button"
                  className={styles.ledgerRow}
                  data-tone={l.hazard === null ? 'error' : hazardTone(l.hazard)}
                  data-open={l.last !== null && l.last.id === openId}
                  data-idle={l.count === 0}
                  disabled={l.last === null}
                  title={
                    l.hazard === null
                      ? `${l.name} is in this robot's history but not in its published profile`
                      : l.last === null
                        ? `${l.name} — never issued to ${robotId} in the window`
                        : `#${l.last.id} ${l.name} · ${l.last.status} — ${CMD_HINT[l.last.status]}`
                  }
                  onClick={() => {
                    if (l.last !== null) onPick(l.last.id);
                  }}
                >
                  <span className={styles.ledgerTick} aria-hidden="true" />
                  <span className={styles.ledgerVerb}>
                    {l.name}
                    {l.hazard === null && <span className={styles.ledgerOff}> off-profile</span>}
                  </span>
                  <span className={styles.ledgerNum}>{l.count === 0 ? NO_VALUE : l.count}</span>
                  {l.last === null ? (
                    <span className={styles.ledgerNever}>never issued</span>
                  ) : (
                    <span className={styles.ledgerLast}>
                      <StatusDot tone={tone ?? 'neutral'} />
                      {l.last.status}
                    </span>
                  )}
                  <span className={styles.ledgerNum}>
                    {l.last === null ? NO_VALUE : duration(now - Date.parse(l.last.updated_at))}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </Panel>
  );
}

/* --------------------------------------------------------------------------
   One ledger row. 26px, one line, a 2px state edge at the leading edge, the
   four-slot stage track, and time-in-state right-aligned in mono — the same
   row the fleet queue draws, minus the column this page's title already is.
   -------------------------------------------------------------------------- */

function HistoryRow({
  cmd,
  now,
  ladder,
  open,
  tabbable,
  onToggle,
}: {
  cmd: LedgerCommand;
  now: number;
  /** The tab's own threshold ladder, measured over this robot's history. */
  ladder: Ladder;
  open: boolean;
  /** Roving tab index: one stop for the whole ledger, arrows walk it. */
  tabbable: boolean;
  onToggle: () => void;
}) {
  const tone = CMD_TONE[cmd.status];
  const detail = detailOf(cmd);
  /* THE LEDGER'S TWO DURATION COLUMNS, INKED AGAINST WHAT THIS ROBOT
     ACTUALLY DOES — not printed flat. `Latency` used to render one grey for
     every row and `In state` one bright white for every row, which made a
     command acked 16h ago typographically identical to one acked 2s ago:
     107 rows and no texture to break. Same renderer, same ladder and the
     same two carriers (colour + a mark in the leading gutter) as the fleet
     board's columns, so one scan finds the stuck row on either surface. */
  const lat = latencyMs(cmd);
  const latB = latencyBand(lat, ladder);
  const inState = now - Date.parse(cmd.updated_at);
  const waitB = waitBand(inState, cmd.status, ladder);
  const fail = failureOf(cmd.result);
  const paramKeys = Object.keys(cmd.params);
  const resultKeys =
    cmd.result === null ? [] : Object.keys(cmd.result).filter((k) => k !== 'error' && k !== 'code');

  const facts: KVItem[] = [
    { label: 'Issued by', value: cmd.issued_by },
    { label: 'Issued', value: utc(cmd.issued_at) },
    { label: 'Updated', value: utc(cmd.updated_at) },
  ];

  /* An expansion that opens below the fold is an expansion the operator has to
     go looking for. `nearest` is a no-op when it already fits, so a row near
     the top of the pane never scrolls out from under the pointer. */
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) panel.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={styles.histRow}
        data-tone={tone}
        data-open={open}
        data-cmd={cmd.id}
        tabIndex={tabbable ? 0 : -1}
        aria-expanded={open}
        title={`#${cmd.id} ${cmd.command} · ${cmd.status} — ${CMD_HINT[cmd.status]}`}
        onClick={onToggle}
      >
        <span className={styles.histEdge} aria-hidden="true" />
        <span className={styles.histId}>{cmd.id}</span>
        <span className={styles.histVerb}>{cmd.command === '' ? NO_VALUE : cmd.command}</span>
        <LifecycleTrack status={cmd.status} />
        <span className={styles.histDetail}>
          {detail.mark !== null && <span className={styles.histCode}>{detail.mark}</span>}
          {detail.text === '' && detail.mark === null ? (
            <span className={styles.histNone}>{NO_VALUE}</span>
          ) : (
            <span className={styles.histText}>{detail.text}</span>
          )}
        </span>
        <span className={`${styles.histBy} ${styles.optBy}`} title={cmd.issued_by}>
          {cmd.issued_by}
        </span>
        <span className={`${styles.histNum} ${styles.optIssued}`} title={utc(cmd.issued_at)}>
          {utcTime(cmd.issued_at)}
        </span>
        <BandedAge
          className={`${styles.histNum} ${styles.optLat}`}
          ms={lat}
          band={latB}
          note={bandNote(latB, ladder, 'latency')}
        />
        <BandedAge
          className={styles.histNum}
          ms={inState}
          band={waitB}
          note={bandNote(waitB, ladder, 'wait')}
        />
      </button>

      {/* Expansion IN PLACE, never a dialog over the thing being read. */}
      {open && (
        <div className={styles.expand} ref={panel}>
          <TransitionTrace cmd={cmd} now={now} />
          {fail !== null && (
            <div className={styles.expandBlock}>
              <div className={styles.expandHead}>
                <Eyebrow>Failure</Eyebrow>
                {fail.code !== null && <span className={styles.expandAside}>{fail.code}</span>}
              </div>
              <p className={styles.expandFail}>{fail.message}</p>
            </div>
          )}
          <div className={styles.expandCols}>
            <div className={styles.expandBlock}>
              <div className={styles.expandHead}>
                <Eyebrow>Command</Eyebrow>
                <span className={styles.expandAside}>{cmd.status}</span>
              </div>
              <KeyValue items={facts} layout="rows" />
            </div>
            <div className={styles.expandBlock}>
              <div className={styles.expandHead}>
                <Eyebrow>Params</Eyebrow>
                <span className={styles.expandAside}>{paramKeys.length}</span>
              </div>
              {paramKeys.length === 0 ? (
                <p className={styles.expandNote}>None — the verb is the whole command.</p>
              ) : (
                <KeyValue
                  layout="rows"
                  items={paramKeys.map((k) => ({ label: k, value: formatParam(k, cmd.params[k]) }))}
                />
              )}
            </div>
            <div className={styles.expandBlock}>
              <div className={styles.expandHead}>
                <Eyebrow>Result</Eyebrow>
                <span className={styles.expandAside}>{resultKeys.length}</span>
              </div>
              {resultKeys.length === 0 ? (
                <p className={styles.expandNote}>
                  {IN_FLIGHT.has(cmd.status) ? 'Not settled yet.' : 'The robot reported no payload.'}
                </p>
              ) : (
                <KeyValue
                  layout="rows"
                  items={resultKeys.map((k) => ({
                    label: k,
                    value: formatParam(k, cmd.result?.[k]),
                  }))}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------------------
   One audit event, at its true timestamp.
   -------------------------------------------------------------------------- */

function ActivityRow({
  ev,
  verb,
  now,
  selected,
  tabbable,
  onPick,
}: {
  ev: CommandEvent;
  verb: string;
  now: number;
  selected: boolean;
  tabbable: boolean;
  onPick: () => void;
}) {
  const tone: Tone = ev.status === null ? 'neutral' : CMD_TONE[ev.status];
  const fail = failureOf(ev.result);

  return (
    <button
      type="button"
      className={styles.actRow}
      data-tone={tone}
      data-selected={selected}
      data-ev={ev.id}
      tabIndex={tabbable ? 0 : -1}
      aria-current={selected ? 'true' : undefined}
      title={`${utc(ev.ts)} · ${ev.event} · #${ev.command_id} · ${ev.operator}`}
      onClick={onPick}
    >
      <span className={styles.actTime}>{utcTime(ev.ts)}</span>
      <span className={styles.actAge}>{duration(now - Date.parse(ev.ts))}</span>
      <span className={styles.actId}>{ev.command_id}</span>
      <span className={styles.actEvent}>
        <StatusDot tone={tone} />
        {ev.event}
      </span>
      <span className={styles.actRest}>
        {verb !== '' && <span className={styles.actVerb}>{verb}</span>}
        {fail !== null && (
          <>
            <span className={styles.actCode}>{fail.code ?? 'error'}</span>
            <span className={styles.actFail}>{fail.message}</span>
          </>
        )}
      </span>
      <span className={styles.actWho} title={ev.operator}>{ev.operator}</span>
    </button>
  );
}

/* ==========================================================================
   DISPATCH STRIP — the C2 write path on a robot's own page.

   The same three rules as the rail bench above (nothing leaves on a click;
   the verb set is the robot's own; the consequence is on screen before the
   commit) rendered for a surface that is 1200px wide and has a ledger under
   it, rather than a 328px rail that has to carry the whole story.

   So it is an INSTRUMENT ROW, not a button tray:

     · every verb the profile publishes on ONE 26px row, in the wire enum, in
       mono, grouped by the hazard class the profile itself declares, with a
       16px seam between classes and no seam inside one — a segmented control
       per class, the way a mode strip is drawn;
     · no glyphs. A transport mark on `pause` and `stop` and none on
       `stop_mapping` is three visual languages for one row (THE BAR §8 tell
       8); the class tick over the group carries the state instead;
     · the lease is CHROME. `Unlock` is a filled verb pinned to the row's
       right edge that actually takes the authority — the same authority the
       identity row's switch takes, one control rendered where it is needed —
       and the gated verbs are inert grey until it is held, then ringed in
       the accent (inorbit-teleop-map-console.png);
     · a click STAGES. Parameters open as typed fields under the row and a
       pinned, full-width `Cancel` / `Send` pair commits
       (formant-3d-waypoint-path-dispatch.png). Nothing reaches a robot on
       one click, benign or not;
     · the RECEIPT is at the point of action: the chip the operator pressed
       carries `#108 pending 0s` and ticks its time-in-state until the
       command settles, so the answer to "did that send" is under the cursor
       and not in a table 400px below it.

   At rest the whole control is one panel header plus two 26px rows.
   ========================================================================== */

/** The lifecycle of the last command THIS control issued. */
interface Receipt {
  id: number;
  verb: string;
  /** The row as the POST returned it — used until the ledger carries it. */
  seed: Command;
}

export function DispatchStrip({
  robotId,
  lease,
  className,
  onIssued,
}: {
  robotId: string;
  /** The page's actuation lease. This control takes and releases it. */
  lease: LeaseState;
  className?: string | undefined;
  onIssued?: ((id: number) => void) | undefined;
}) {
  const robotQ = useRobot(robotId);
  const historyQ = useRobotCommands(robotId);
  const { data: robot } = robotQ;
  const { data: history } = historyQ;
  const { connected } = useCommandStream(robotId);
  const issue = useIssueCommand();
  const refresh = useRefreshCommands();
  const clock = useNow();

  const feed = health(
    [
      source(`/api/robots/${robotId}`, 5_000, robotQ),
      source(`/api/robots/${robotId}/commands`, 20_000, historyQ),
    ],
    clock,
  );
  const robotRead = useRead(robotQ, {
    path: `/api/robots/${robotId}`,
    everyMs: 5_000,
    subject: robotId,
  });
  const historyRead = useRead(historyQ, {
    path: `/api/robots/${robotId}/commands`,
    everyMs: 20_000,
    subject: 'the command history',
  });
  /* Ages off the last good read, never the wall clock: a frozen poll must not
     keep a receipt's time-in-state counting as though it were live. */
  const now = feed.asOf;

  const [staged, setStaged] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  const profile = useMemo(() => readProfile(robot?.command_profile), [robot?.command_profile]);
  const version = profileVersion(robot?.command_profile);

  /* THE CLASSES THE PROFILE ITSELF PUBLISHES, in its own order — never a fixed
     two, because a class the agent invents later must get its own group rather
     than be filed under the safest one on screen. `readProfile` has already
     ranked them benign → motion → hazardous.

     Within a class the ABORT GOES LAST: `stop` is the verb that ends whatever
     is running, and the references put an abort at the end of its verb row
     (anduril-lattice-asset-detail-tasking.png: `Edit │ Complete │ ✕ Cancel`).
     Alphabetically it lands between `start_capture` and `stop_capture`, which
     reads as one of the capture verbs. */
  const classes = useMemo(() => {
    const out: { hazard: string; verbs: ProfileVerb[] }[] = [];
    for (const v of profile ?? []) {
      const last = out[out.length - 1];
      if (last !== undefined && last.hazard === v.hazard) last.verbs.push(v);
      else out.push({ hazard: v.hazard, verbs: [v] });
    }
    for (const g of out) {
      g.verbs.sort((a, b) => (a.name === 'stop' ? 1 : 0) - (b.name === 'stop' ? 1 : 0));
    }
    return out;
  }, [profile]);

  /* Drawing order IS numbering order, so a printed digit can never point at a
     different chip from the one beside it. */
  const verbs = useMemo(() => classes.flatMap((g) => g.verbs), [classes]);

  /* Standing down on a target change is not a nicety — a staged motion verb
     that survived it would be sent to whatever machine came next. */
  useEffect(() => {
    setStaged(null);
    setDrafts({});
    setReceipt(null);
  }, [robotId]);

  const verb = useMemo(
    () => (staged === null ? null : verbs.find((v) => v.name === staged) ?? null),
    [staged, verbs],
  );

  const lastOfVerb = useMemo(
    () => (staged === null ? null : (history ?? []).find((c) => c.command === staged) ?? null),
    [history, staged],
  );

  const rows = useMemo<ParamRow[]>(() => {
    if (verb === null) return [];
    const observed = lastOfVerb?.params ?? {};
    const names = [...new Set([...verb.required, ...Object.keys(observed)])];
    return names.map((name) => {
      const spec = specFor(name, observed[name]);
      return {
        name,
        spec,
        required: verb.required.includes(name),
        seed:
          observed[name] !== undefined && lastOfVerb !== null
            ? `from #${lastOfVerb.id}`
            : spec.kind === 'latlon' && robot?.lat != null
              ? `from last fix, ${relative(robot.last_seen, now)}`
              : null,
      };
    });
  }, [verb, lastOfVerb, robot?.lat, robot?.last_seen, now]);

  const stage = useCallback(
    (v: ProfileVerb) => {
      const own = (history ?? []).find((c) => c.command === v.name);
      const observed = own?.params ?? {};
      const names = [...new Set([...v.required, ...Object.keys(observed)])];
      const next: Record<string, Draft> = {};
      for (const name of names) {
        const spec = specFor(name, observed[name]);
        let seed: unknown = observed[name];
        if (seed === undefined && spec.kind === 'latlon' && robot?.lat != null && robot.lon != null) {
          seed = { lat: robot.lat, lon: robot.lon };
        }
        next[name] = draftFor(spec, seed);
      }
      setStaged(v.name);
      setDrafts(next);
      /* The previous dispatch's receipt is the previous dispatch's. Staging
         is the operator moving on from it. */
      setReceipt(null);
    },
    [history, robot?.lat, robot?.lon],
  );

  const standDown = useCallback(() => {
    setStaged(null);
    setDrafts({});
  }, []);

  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const r of rows) {
      const d = drafts[r.name];
      if (d === undefined) {
        if (r.required) out[r.name] = 'required';
        continue;
      }
      if (!r.required && isBlank(d)) continue;
      const c = coerce(r.spec, d);
      if (!c.ok) out[r.name] = c.why;
    }
    return out;
  }, [rows, drafts]);

  const invalid = Object.keys(errors).length > 0;
  const gated = verb !== null && verb.hazard !== 'benign';
  const ready = verb !== null && !invalid && (!gated || lease.held) && !issue.isPending;
  const blockedBecause =
    verb === null
      ? 'no verb staged'
      : invalid
        ? 'a parameter is missing or out of range'
        : gated && !lease.held
          ? 'actuation lease not held'
          : issue.isPending
            ? 'in flight'
            : null;

  const send = useCallback(() => {
    if (verb === null || !ready) return;
    const params: Record<string, unknown> = {};
    for (const r of rows) {
      const d = drafts[r.name];
      if (d === undefined) continue;
      if (!r.required && typeof d === 'string' && d.trim() === '') continue;
      const c = coerce(r.spec, d);
      if (c.ok) params[r.name] = c.value;
    }
    issue.mutate(
      { robotId, command: verb.name as CommandName, params },
      {
        onSuccess: (cmd) => {
          /* The toast is the console-wide receipt; THIS is the one at the
             point of action, and it is the one the operator is looking at. */
          notifyOk(`${cmd.command} queued for ${cmd.robot_id}`, `#${cmd.id} · pending`);
          refresh(robotId);
          onIssued?.(cmd.id);
          setReceipt({ id: cmd.id, verb: cmd.command, seed: cmd });
          standDown();
        },
      },
    );
  }, [verb, ready, rows, drafts, issue, robotId, refresh, onIssued, standDown]);

  /* The receipt's LIVE row: the ledger's copy the moment it carries one, the
     POST's own response until then. Never a status this control invented. */
  const receiptRow = useMemo<Command | null>(() => {
    if (receipt === null) return null;
    return (history ?? []).find((c) => c.id === receipt.id) ?? receipt.seed;
  }, [receipt, history]);

  /* ---- keyboard ---------------------------------------------------------
     `⌘⏎` commits, `Esc` stands down, `u` takes or releases the lease. Each is
     printed on the control it drives, the way a device menu prints its
     shortcuts inline in Formant.

     THE NUMBER ROW IS NOT OURS TO TAKE HERE. The shell binds `1`–`6` to
     surface switching everywhere except `/commands`, where it stands down for
     the dispatch bench (shell/keyboard.ts). Binding digits on a robot's page
     as well would fire both listeners on one press — the operator would stage
     a verb AND navigate away, and could not tell which key they had thrown.
     So the digits are live only where the shell has yielded them, and the
     chips print a number only there; everywhere else the verb row is one tab
     stop that the arrow keys walk, which is the console's own convention for
     a segmented control and is already on the key sheet. */
  const digitsFree =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/commands');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && staged !== null) {
        standDown();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (ready) {
          e.preventDefault();
          send();
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === 'u' || e.key === 'U') {
        if (verbs.some((v) => v.hazard !== 'benign')) {
          e.preventDefault();
          if (lease.held) lease.onRelease();
          else lease.onAcquire();
        }
        return;
      }
      if (!digitsFree || !/^[0-9]$/.test(e.key)) return;
      const n = e.key === '0' ? 10 : Number(e.key);
      const v = verbs[n - 1];
      if (v === undefined) return;
      e.preventDefault();
      stage(v);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [verbs, stage, staged, standDown, ready, send, lease, digitsFree]);

  const hasGated = verbs.some((v) => v.hazard !== 'benign');

  /* What a staged command will land BEHIND. A count says there is traffic; a
     list says whether the traffic contradicts what is about to be sent — a
     `resume` queued behind an unacked `stop` is the case that matters, and it
     is only visible as rows. It sits beside the commit because that is the
     moment it qualifies. */
  const ahead = useMemo(
    () => (history ?? []).filter((c) => IN_FLIGHT.has(c.status)).slice(0, 4),
    [history],
  );
  const inFlightCount = useMemo(
    () => (history ?? []).filter((c) => IN_FLIGHT.has(c.status)).length,
    [history],
  );

  const checks = useMemo<Check[]>(() => {
    if (robot === undefined) return [];
    return [
      {
        key: 'link',
        label: 'link',
        value: LIVENESS_STATE[robot.liveness].label,
        tone: linkCheckTone(robot.liveness),
        note: robot.liveness === 'live' && robot.last_seen !== null ? relative(robot.last_seen, now) : null,
      },
      {
        key: 'mode',
        label: 'mode',
        value: STATUS_STATE[robot.status].label,
        tone: matrixTone(STATUS_STATE[robot.status].tone),
        note: robot.status === 'error' ? 'motion will be rejected' : null,
      },
      {
        key: 'battery',
        label: 'battery',
        value: robot.battery_pct === null ? NO_VALUE : `${robot.battery_pct.toFixed(0)}%`,
        tone:
          robot.battery_pct === null
            ? 'neutral'
            : robot.battery_pct <= 15
              ? 'error'
              : robot.battery_pct <= 25
                ? 'warn'
                : 'nominal',
        note: null,
      },
    ];
  }, [robot, now]);

  /* One chip, drawn identically in every state it can be in. */
  const chip = (v: ProfileVerb) => {
    const slot = verbs.indexOf(v);
    const active = v.name === staged;
    const locked = v.hazard !== 'benign' && !lease.held;
    const mine = receiptRow !== null && receipt?.verb === v.name;
    return (
      <button
        key={v.name}
        type="button"
        /* A single-select over one row of segments — the console's shared
           composite driver (components/motion/widgets.ts) then gives the row
           one tab stop and the arrow keys, exactly as it does for every other
           segmented control here. */
        role="radio"
        aria-checked={active}
        className={styles.chip}
        data-tone={hazardTone(v.hazard)}
        data-active={active}
        data-locked={locked}
        data-armed={v.hazard !== 'benign' && lease.held}
        disabled={locked}
        /* A disabled control has to say WHY out loud, not only on a tooltip a
           screen reader will not reach. */
        aria-label={
          locked
            ? `${v.name} — ${v.hazard} class, locked until actuation authority is held`
            : `${v.name} — ${hazardConsequence(v.hazard)}${
                v.required.length === 0 ? '' : `, requires ${v.required.join(', ')}`
              }`
        }
        title={
          locked
            ? `${v.name} is ${v.hazard} class — press Unlock to take actuation authority first`
            : `Stage ${v.name} — ${hazardConsequence(v.hazard)}${
                v.required.length === 0 ? '' : `. Requires ${v.required.join(', ')}.`
              }`
        }
        onClick={() => (active ? standDown() : stage(v))}
      >
        <span className={styles.chipName}>{v.name}</span>
        {v.required.length > 0 && (
          <span
            className={styles.chipReq}
            aria-hidden="true"
            title={`requires ${v.required.join(', ')}`}
          >
            ·
          </span>
        )}
        {mine && receiptRow !== null ? (
          <span
            className={styles.chipReceipt}
            data-tone={CMD_TONE[receiptRow.status]}
            title={`#${receiptRow.id} — ${CMD_HINT[receiptRow.status]}. In ${receiptRow.status} for ${duration(
              Math.max(0, now - Date.parse(receiptRow.updated_at)),
            )}.`}
          >
            <span className={styles.chipReceiptId}>#{receiptRow.id}</span>
            <span className={styles.chipReceiptState}>{receiptRow.status}</span>
            <span className={styles.chipReceiptAge}>
              {duration(Math.max(0, now - Date.parse(receiptRow.updated_at)))}
            </span>
          </span>
        ) : (
          digitsFree && slot < 10 && <kbd className={styles.chipKbd}>{(slot + 1) % 10}</kbd>
        )}
      </button>
    );
  };

  return (
    <Panel
      className={className === undefined ? styles.strip : `${styles.strip} ${className}`}
      title="Issue command"
      meta={
        <>
          <span className={styles.stripProfile} title="The command contract this build publishes">
            {robot === undefined
              ? NO_VALUE
              : `${robot.morphology}${version === null ? '' : ` ${version}`}`}
          </span>
          <span className={styles.stripCount}>{figure(robotRead, verbs.length)} verbs</span>
          <FeedTag health={feed} now={clock} pulse={connected} read={[robotRead, historyRead]} />
        </>
      }
      pad="none"
    >
      {unread([robotRead, historyRead]) && <FaultLine read={robotRead} />}
      {profile === null ? (
        <div className={styles.stripAwait}>
          <Awaiting
            label="No command profile published"
            detail={`${robotId} advertises its verb set on enrolment; nothing has arrived yet.`}
          />
        </div>
      ) : (
        <>
          {/* ---- THE VERB ROW ------------------------------------------- */}
          <div className={styles.row}>
            {/* The verb set wraps among itself when the host is narrow; the
                lease verb beside it never does — an authority control that
                can be pushed onto its own line is an authority control the
                operator can lose track of. */}
            <div
              className={styles.rowVerbs}
              role="radiogroup"
              aria-label={`Commands ${robotId} accepts`}
            >
              {classes.map((g) => (
                <div key={g.hazard} className={styles.group} data-tone={hazardTone(g.hazard)}>
                  <span
                    className={styles.groupWord}
                    data-tone={hazardTone(g.hazard)}
                    title={hazardConsequence(g.hazard)}
                  >
                    {g.hazard}
                    <span className={styles.groupCount}>{g.verbs.length}</span>
                  </span>
                  <div className={styles.chips}>{g.verbs.map(chip)}</div>
                </div>
              ))}
            </div>

            {/* ---- THE LEASE, AS CHROME --------------------------------
                Not a corner label saying what is missing — the verb that
                fixes it, pinned to the row's right edge. It drives the same
                page lease the identity-row switch drives, so this is one
                authority rendered where it is used, not a second one to
                take. */}
            {hasGated && (
              <button
                type="button"
                role="switch"
                aria-checked={lease.held}
                className={styles.unlock}
                data-held={lease.held}
                aria-label={
                  lease.held
                    ? `Actuation authority held for ${robotId}. Press to release it.`
                    : `Take actuation authority for ${robotId}. Four verbs need it.`
                }
                aria-keyshortcuts="u"
                /* ONE FILLED VERB AT A TIME. The accent marks the single
                   thing to press next: the lease while it is what blocks the
                   dispatch, the commit the moment the commit can fire. */
                data-primary={!lease.held && !ready}
                title={
                  lease.held
                    ? `Actuation authority held${
                        lease.msLeft === null ? '' : ` — releases in ${duration(lease.msLeft)}`
                      }. Click to release it now.`
                    : `Take actuation authority. ${
                        verbs.filter((v) => v.hazard !== 'benign').length
                      } of ${verbs.length} verbs on ${robotId} drive or actuate it. Taken from ${lease.where}.`
                }
                onClick={() => (lease.held ? lease.onRelease() : lease.onAcquire())}
              >
                <Icon name={lease.held ? 'unlock' : 'lock'} size={14} />
                <span className={styles.unlockWord}>{lease.held ? 'Held' : 'Unlock'}</span>
                {lease.msLeft !== null && (
                  <span className={styles.unlockLeft}>{duration(lease.msLeft)}</span>
                )}
                <kbd className={styles.chipKbd}>U</kbd>
              </button>
            )}
          </div>

          {/* ---- THE STAGED COMMAND ------------------------------------- */}
          {verb !== null && (
            <div className={styles.bench}>
              {rows.length > 0 && (
                <div className={styles.benchParams}>
                  {rows.map((r) => (
                    <ParamField
                      key={r.name}
                      row={r}
                      value={drafts[r.name] ?? draftFor(r.spec, undefined)}
                      error={errors[r.name] ?? null}
                      onChange={(next) => setDrafts((d) => ({ ...d, [r.name]: next }))}
                    />
                  ))}
                </div>
              )}
              <div className={styles.benchLine}>
                <span className={styles.benchVerb}>{verb.name}</span>
                <span className={styles.benchArrow}>→</span>
                <span className={styles.benchRobot}>{robotId}</span>
                {rows.length > 0 && (
                  <span className={styles.benchParamsEcho}>{previewParams(rows, drafts)}</span>
                )}
                <span className={styles.benchSpacer} />
                {lastOfVerb !== null && (
                  <span className={styles.benchPrec} data-tone={CMD_TONE[lastOfVerb.status]}>
                    <StatusDot tone={CMD_TONE[lastOfVerb.status]} />
                    last #{lastOfVerb.id} {lastOfVerb.status} {relative(lastOfVerb.updated_at, now)}
                  </span>
                )}
                <span className={styles.benchState} data-blocked={blockedBecause !== null}>
                  {blockedBecause === null ? 'staged, not sent' : blockedBecause}
                </span>
              </div>
              {/* The commit — two halves the same size, and beside them the
                  queue this command is about to join. A control that can lose
                  work never shares a cell with one that reaches a robot, but
                  the fact that qualifies the press belongs at eye level with
                  it. */}
              <div className={styles.benchCommit}>
                <div className={styles.pair}>
                  <Button size="sm" variant="secondary" block kbd="Esc" onClick={standDown}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant={ready ? 'primary' : 'secondary'}
                    block
                    disabled={!ready}
                    kbd="⌘⏎"
                    data-kbd="commands-send"
                    title={
                      blockedBecause === null
                        ? `Send ${verb.name} to ${robotId}`
                        : `Cannot send — ${blockedBecause}`
                    }
                    onClick={send}
                  >
                    Send
                  </Button>
                </div>
                <div className={styles.benchAhead}>
                  <span className={styles.benchAheadWord}>
                    ahead of this
                    <span className={styles.groupCount}>{figure(historyRead, inFlightCount)}</span>
                  </span>
                  {ahead.length === 0 ? (
                    <span className={styles.benchAheadNone}>
                      nothing outstanding on {robotId}
                    </span>
                  ) : (
                    ahead.map((c) => (
                      <span
                        key={c.id}
                        className={styles.benchAheadRow}
                        data-tone={CMD_TONE[c.status]}
                        title={`#${c.id} ${c.command} — ${CMD_HINT[c.status]}`}
                      >
                        <StatusDot tone={CMD_TONE[c.status]} />
                        <span className={styles.benchAheadId}>#{c.id}</span>
                        <span className={styles.benchAheadVerb}>{c.command}</span>
                        <span className={styles.benchAheadState}>{c.status}</span>
                        <span className={styles.benchAheadAge}>
                          {duration(Math.max(0, now - Date.parse(c.updated_at)))}
                        </span>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ---- THE STANDING LINE ---------------------------------------
              Pre-flight on the left — the robot's live condition in its own
              readings, which is what a motion verb is judged against — and
              on the right either the receipt for the last dispatch or the
              consequence of the staged one. One 20px line, always the same
              height, so nothing on this control moves when data arrives. */}
          <div className={styles.foot}>
            <div className={styles.checks} data-stale={!feed.live}>
              {checks.map((c) => (
                <span key={c.key} className={styles.check} data-tone={c.tone}>
                  <StatusDot tone={c.tone} hollow={c.tone === 'offline'} />
                  <span className={styles.checkLabel}>{c.label}</span>
                  <span className={styles.checkValue}>{c.value}</span>
                  {c.note !== null && <span className={styles.checkNote}>{c.note}</span>}
                </span>
              ))}
              {!feed.live && (
                <span className={styles.checkStale}>
                  as of {utcTime(new Date(feed.lastGoodAt ?? clock).toISOString())}
                </span>
              )}
            </div>
            <span className={styles.footSpacer} />
            {issue.isError ? (
              <span className={styles.footError}>{String(issue.error)}</span>
            ) : receiptRow !== null ? (
              <span className={styles.footReceipt} data-tone={CMD_TONE[receiptRow.status]}>
                <StatusDot tone={CMD_TONE[receiptRow.status]} />
                <span className={styles.footReceiptId}>#{receiptRow.id}</span>
                <span className={styles.footReceiptVerb}>{receiptRow.command}</span>
                <span className={styles.footReceiptState}>{receiptRow.status}</span>
                <span className={styles.footReceiptAge}>
                  {duration(Math.max(0, now - Date.parse(receiptRow.updated_at)))} in state
                </span>
                <span className={styles.footReceiptWhy}>{detailOf(receiptRow).text}</span>
              </span>
            ) : (
              <span className={styles.footHint} data-tone={verb === null ? undefined : hazardTone(verb.hazard)}>
                {verb === null
                  ? digitsFree
                    ? 'Pick a verb, or press its number.'
                    : 'Pick a verb. Nothing is sent until the commit.'
                  : hazardConsequence(verb.hazard)}
              </span>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}

/* ==========================================================================
   COMMAND ACTIVITY — this robot's slice of the lifecycle log.

   The ledger above it is one row per COMMAND and answers "what is
   outstanding"; this is one row per TRANSITION at its true timestamp and
   answers "what has been happening". They are different questions and the
   references keep them as different panels (inorbit-robot-detail-dense.png's
   audit log under its mission block).

   Straight off `/api/audit/commands` — the same read the fleet board runs,
   deduped by query key, so it costs no request — filtered to this machine and
   newest first. The log's own freshness is answered here rather than
   inherited: it polls at 3s where the ledger polls at 20s, and one light for
   two cadences is a defect (THE BAR §13 tell 27).
   ========================================================================== */

export function CommandActivity({
  robotId,
  className,
  onPick,
  selectedId,
}: {
  robotId: string;
  className?: string | undefined;
  onPick?: ((commandId: number) => void) | undefined;
  selectedId?: number | null | undefined;
}) {
  const auditQ = useCommandAudit();
  const historyQ = useRobotCommands(robotId);
  const { connected } = useCommandStream(robotId);
  const clock = useNow();

  const feedH = health([source('/api/audit/commands', 3_000, auditQ)], clock);
  const auditRead = useRead(auditQ, {
    path: '/api/audit/commands',
    everyMs: 3_000,
    subject: 'the command ledger',
  });
  const now = feedH.asOf;

  const feed = useMemo(() => {
    const verbOf = new Map((historyQ.data ?? []).map((c) => [c.id, c.command]));
    return (auditQ.data ?? [])
      .filter((e) => e.robot_id === robotId)
      .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts) || b.id - a.id)
      .slice(0, ROBOT_FEED_CAP)
      .map((ev) => ({ ev, verb: verbOf.get(ev.command_id) ?? '' }));
  }, [auditQ.data, historyQ.data, robotId]);

  const onFeedKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const list = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-ev]')];
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (i === -1) return;
    e.preventDefault();
    const next = list[e.key === 'ArrowDown' ? Math.min(list.length - 1, i + 1) : Math.max(0, i - 1)];
    next?.focus();
  }, []);

  return (
    <Panel
      className={className}
      title="Activity"
      count={figure(auditRead, feed.length)}
      meta={
        <>
          <span
            className={styles.tabMeta}
            title={`This robot's slice of /api/audit/commands, newest first. The board reads the newest ${AUDIT_WINDOW} events fleet-wide.`}
          >
            audit
          </span>
          <FeedTag health={feedH} now={clock} pulse={connected} read={auditRead} />
        </>
      }
      pad="none"
      scroll={false}
    >
      <div className={styles.actFrame}>
        {!feedH.live && <StaleLine health={feedH} now={clock} read={auditRead} />}
        <div className={styles.actHead} role="row">
          <span>Time</span>
          <span className={styles.right}>Age</span>
          <span className={styles.right}>#</span>
          <span>Event</span>
          <span>Verb · reason</span>
          <span className={styles.right}>By</span>
        </div>
        <div className={styles.actScroll} data-stale={!feedH.live} onKeyDown={onFeedKey}>
          {unread(auditRead) ? (
            <FaultLine read={auditRead} />
          ) : feed.length === 0 ? (
            <Empty>No lifecycle events for {robotId} in the audit window.</Empty>
          ) : (
            feed.map(({ ev, verb }, i) => (
              <ActivityRow
                key={ev.id}
                ev={ev}
                verb={verb}
                now={now}
                selected={ev.command_id === selectedId}
                tabbable={i === 0}
                onPick={() => onPick?.(ev.command_id)}
              />
            ))
          )}
        </div>
      </div>
    </Panel>
  );
}
